import { useEffect, useId, useMemo, useRef, useState } from "react";
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import Highcharts from '../../highcharts';
import XRangeModule from 'highcharts/modules/xrange';
import ExportingModule from 'highcharts/modules/exporting';
import { toPng } from 'html-to-image';
import Header from '../../components/Header';
import AccessibleChart from '../../components/AccessibleChart';
import ResponsiveTableRegion from '../../components/ResponsiveTableRegion';
import SearchableSelect from '../../components/SearchableSelect';
import AsyncStatus from '../../components/AsyncStatus';
import { ventrisConfig } from '../../ventris.config';

import ReportPageIcon from '../../icons/ReportPageIcon';
import DownloadIcon from '../../icons/DownloadIcon';
import FilterIcon from '../../icons/FilterIcon';
import logo from '../../assets/img/logo.jpg';
import wraLogo from '../../assets/img/wra.png';
import { getUseCaseTerminology } from '../../useCase';
import {
  SCREENING_RISK_CONTENT,
  SCREENING_RISK_CUTPOINTS,
  ble,
  del,
  nre,
  wre
} from './ClinicianReport';

XRangeModule(Highcharts);
ExportingModule(Highcharts);

type SearchOption = {
  value: string | number;
  label: string;
};

type ReportRequest = {
  mode: "student" | "class" | "group";
  timeframe: "recent" | "ytd" | "range";
  populationLabel: string;
  studentIds: string[];
  students: any[];
  startDate?: string;
  endDate?: string;
};

type NarrativeProfileRecord = {
  screening: "High" | "Moderate" | "Low";
  phon?: "low" | "ok";
  decoding?: "low" | "ok";
  word?: "low" | "ok";
  interpretation: string;
  recommendation: string;
};

type NarrativeSectionBlock = {
  title?: string;
  body: string;
};

type NarrativeSectionData = {
  id: string;
  title: string;
  blocks: NarrativeSectionBlock[];
};

type ExportTableColumn = {
  key: string;
  label: string;
};

const SCHOOL_YEAR_START_MONTH = 6;
const BLUE_THEME = '#26306C';
const MODERATE_THEME = '#2f69a2';
const WRA_NARRATIVE_PROFILES = {
  KF: [
    {
      screening: "High",
      phon: "low",
      interpretation: "The WRA screening algorithms indicate a high risk of future reading challenges. The subtests show some difficulty processing and manipulating the sound structure of spoken language.",
      recommendation: "Results from both the screener and subtests indicate a need for further evaluation and progress monitoring of reading difficulty. Additional instruction in phonological awareness (the ability to process and manipulate the sound structure of spoken language) and phonics (connecting letters to their sounds) is recommended."
    },
    {
      screening: "Moderate",
      phon: "low",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading challenges. The subtests show some difficulty processing and manipulating the sound structure of spoken language.",
      recommendation: "Results from both the screener and subtests indicate a need for further evaluation and progress monitoring of reading difficulty. Additional instruction in phonological awareness (the ability to process and manipulate the sound structure of spoken language) and phonics (connecting letters to their sounds) is recommended."
    },
    {
      screening: "Low",
      phon: "low",
      interpretation: "The WRA screening algorithms indicate a low risk of future reading challenges. However, the subtests show some difficulty processing and manipulating the sound structure of spoken language.",
      recommendation: "The screener suggests that this student will most likely test on grade level in reading by the end of the year. However, it is important to consider the low scores on the phonological awareness tasks as potentially indicating a risk of future reading difficulties. Additional instruction in foundational reading skills, especially phonological awareness and phonics, is recommended."
    },
    {
      screening: "High",
      phon: "ok",
      interpretation: "The WRA screening algorithms indicate a high risk of future reading difficulty. However, the subtests show typical development of the ability to process and manipulate the sound structure of language, which is a key foundational skill for reading.",
      recommendation: "The screener indicates the need for further evaluation for reading difficulties. Additional instruction in foundational literacy skills, such as phonological awareness and phonics, is recommended. Also, additional opportunities to practice reading connected texts would help the student build reading vocabulary."
    },
    {
      screening: "Moderate",
      phon: "ok",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading difficulty. However, the subtests show typical development of the ability to process and manipulate the sound structure of language, which is a key foundational skill for reading.",
      recommendation: "The screener indicates the need for further evaluation for reading difficulties. Additional instruction in foundational literacy skills, such as phonological awareness and phonics, is recommended. Also, additional opportunities to practice reading connected texts would help the student build reading vocabulary."
    },
    {
      screening: "Low",
      phon: "ok",
      interpretation: "The WRA screening algorithms indicate a low risk of future reading difficulty. The subtests show typical development of the ability to process and manipulate the sound structure of language, which is a key foundational skill for reading.",
      recommendation: "Results indicate no concerns about the student's reading development at this time. To ensure continued grade-level reading skills, regular screening and progress monitoring are recommended. Like all students, the student would benefit from instruction in foundational literacy skills, such as phonological awareness and phonics, as well as additional opportunities to practice reading connected texts to build reading vocabulary."
    }
  ],
  KW2: [
    {
      screening: "High",
      phon: "low",
      decoding: "low",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a high risk of future reading difficulty. Consistent with this finding are the subtest results, which show difficulty processing and manipulating the sound structure of spoken language, connecting letters to their sounds in print, and reading printed words. Together, these results suggest significant difficulties with reading.",
      recommendation: "Results from both the screener and subtests indicate a need for further evaluation and progress monitoring. In addition, targeted instruction in foundational reading skills is recommended, particularly in phonological awareness and phonics, along with increased opportunities to practice reading connected text to support vocabulary development."
    },
    {
      screening: "Moderate",
      phon: "low",
      decoding: "low",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading challenges. The subtest results show difficulty processing and manipulating the sound structure of spoken language, connecting letters to their sounds in print, and reading printed words. Together, these results suggest difficulties with reading.",
      recommendation: "Both the screener and subtests indicate the need for further evaluation and progress monitoring of reading difficulties. Also recommended are additional instruction in foundational reading skills, especially phonological awareness and phonics, and as additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Low",
      phon: "low",
      decoding: "low",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a low risk of future reading challenges. However, the subtest results show difficulty processing and manipulating the sound structure of spoken language, connecting letters to their sounds in print, and reading printed words.",
      recommendation: "Although the screener indicates that the student is likely to be reading on grade-level at the end of the year, it is important to consider the low scores on the subtests as potentially indicating a risk of future reading difficulties. Further evaluation for reading difficulties is recommended. Also recommended are additional instruction in foundational reading skills, especially phonological awareness and phonics, and additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "High",
      phon: "low",
      decoding: "low",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a high risk of future reading difficulties. In addition, the subtest results show difficulty processing and manipulating the sound structure of spoken language and connecting letters to their sounds in print. However, word reading ability is a relative strength.",
      recommendation: "The screener indicates that students with similar results struggled with future reading difficulties. Consider further evaluations and/or progress monitoring to ensure continued development. Also recommended are additional instruction in foundational reading skills, especially phonological awareness and phonics, and additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Moderate",
      phon: "low",
      decoding: "low",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading difficulty. In addition, the subtest results show difficulty processing and manipulating the sound structure of spoken language and connecting letters to their sounds in print. However, word reading ability is a relative strength.",
      recommendation: "Taking the screening and subtest results as a whole, further evaluation and progress monitoring for reading difficulties is recommended. Also recommended are additional instruction in foundational reading skills, especially phonological awareness and phonics, and additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Low",
      phon: "low",
      decoding: "low",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a low risk of future reading difficulty. However, the subtest results show difficulty processing and manipulating the sound structure of spoken language and connecting letters to their sounds in print. Yet, word reading ability is a relative strength, which is consistent with the low risk categorization.",
      recommendation: "Taking the screening and subtest results as a whole, it is recommended that this student receive further evaluation for reading difficulties. Also recommended is additional instruction in foundational reading skills, especially phonological awareness and phonics, as well as additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "High",
      phon: "low",
      decoding: "ok",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a high risk of future reading difficulty. The subtests show difficulty processing and manipulating the sound structure of spoken language and reading printed words. The ability to connect letters to their sounds in print is a relative strength.",
      recommendation: "The screener indicates that students with similar results struggled with future reading difficulties. Taking the screening and subtest results as a whole, it is recommended that this student receive further evaluation and progress monitoring of reading difficulties. Also recommended are additional instruction in foundational reading skills, especially phonological awareness, and additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Moderate",
      phon: "low",
      decoding: "ok",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading difficulty. The subtests show difficulty processing and manipulating the sound structure of spoken language and reading printed words. The ability to connect letters to their sounds in print is a relative strength.",
      recommendation: "Taking the screening and subtest results as a whole, further evaluation and progress monitoring for reading difficulties is recommended. Also recommended are additional instruction in foundational reading skills, especially phonics, and additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Low",
      phon: "low",
      decoding: "ok",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a low risk of future reading difficulty. The subtests show difficulty processing and manipulating the sound structure of spoken language and reading printed words. The ability to connect letters to their sounds in print is a relative strength.",
      recommendation: "The screener suggests that this student will most likely test on grade level by the end of the year. However, low scores on the phonological awareness and word reading tasks suggests this student would benefit from additional instruction and progress monitoring in foundational reading skills, especially phonics, as well as additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "High",
      phon: "low",
      decoding: "ok",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a high risk of future reading difficulty. The subtests indicate difficulty processing and manipulating the sound structure of spoken language. But connecting letters to their sounds and word reading are relative strengths.",
      recommendation: "The screener indicates that students with similar scores at this time of year are at a high risk of significantly struggling with reading at end of the school year. Further evaluation and progress monitoring of reading difficulties is recommended. Instructionally, this student may benefit from additional focus on phonological awareness."
    },
    {
      screening: "Moderate",
      phon: "low",
      decoding: "ok",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading difficulty. The subtests indicate difficulty processing and manipulating the sound structure of spoken language. But connecting letters to their sounds and word reading generally are relative strengths.",
      recommendation: "While the screener indicates moderate risk of future reading difficulties, the subtest results paint a mixed picture, with only phonological awareness showing difficulties. Given this, additional evaluation and progress monitoring of foundational reading skills may be warranted to further assess reading difficulties. Instructionally, this student may benefit from additional focus on phonological awareness."
    },
    {
      screening: "Low",
      phon: "low",
      decoding: "ok",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a low risk of future reading difficulty. The subtests indicate difficulty processing and manipulating the sound structure of spoken language. But connecting letters to their sounds and word reading generally are relative strengths.",
      recommendation: "Taking the screening and subtest results as a whole, this student appears to be developing reading skills at a rate appropriate for their grade. However, low phonological awareness warrants monitoring and potentially further evaluating, as this skill is a critical foundational skill for fluent reading."
    },
    {
      screening: "High",
      phon: "ok",
      decoding: "low",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a high risk of future reading difficulty. The subtests show difficulty connecting letters to their sounds in print and reading printed words. However, hearing and manipulating the sound structure of spoken language is a relative strength.",
      recommendation: "Both the screener and subtests indicate the need for further evaluation and progress monitoring of reading difficulties. Also recommended are additional instruction in foundational reading skills, especially phonics, and additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Moderate",
      phon: "ok",
      decoding: "low",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading difficulty. The subtests show difficulty connecting letters to their sounds in print and reading printed words. However, hearing and manipulating the sound structure of spoken language is a relative strength.",
      recommendation: "Both the screener and subtests indicate the need for further evaluation and progress monitoring of reading difficulties. Also recommended is additional instruction in foundational reading skills, especially phonics, as well as additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Low",
      phon: "ok",
      decoding: "low",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a low risk of future reading difficulty. The subtests show difficulty connecting letters to their sounds in print and reading printed words. However, hearing and manipulating the sound structure of spoken language is a relative strength.",
      recommendation: "The screener suggests that this student will most likely test on grade level by the end of the year. However, it is important to consider the low scores on the phonological awareness and word reading tasks as potentially indicating a risk of future reading difficulties. Further evaluation for reading difficulties is recommended. Also recommended are additional instruction in foundational reading skills, especially phonics, and additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "High",
      phon: "ok",
      decoding: "low",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a high risk of future reading difficulty. The subtests show difficulty with connecting letters to their sounds in print, but hearing and manipulating the sound structure of spoken language and word reading are relative strengths.",
      recommendation: "The screener indicates that students with similar scores at this time of year are at a high risk of significantly struggling with reading at end of the school year. The subtest results show that the only area where the student is having significant difficulties is connecting letters to their sounds in print. Given the mixed results, additional evaluation and progress monitoring of foundational reading skills may be warranted to further assess risk of reading difficulties. Instructionally, this student may benefit from additional focus on phonics as well as additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Moderate",
      phon: "ok",
      decoding: "low",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading difficulty. The subtests show difficulty with connecting letters to their sounds in print, but hearing and manipulating the sound structure of spoken language and word reading are relative strengths.",
      recommendation: "Taking the screening and subtest results as a whole, further evaluation and progress monitoring of reading difficulties is recommended. Instructionally, this student may benefit from additional focus on phonics as well as additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Low",
      phon: "ok",
      decoding: "low",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a low risk of future reading difficulty. The subtests show difficulty with connecting letters to their sounds in print, but hearing and manipulating the sound structure of spoken language and word reading are relative strengths.",
      recommendation: "Taking the screening and subtest results as a whole, this student appears at low risk for severe reading difficulties. However, the student's low score on the nonword reading task indicates a need for additional instruction and progress monitoring in phonics. Additionally, the student, like all students, would benefit from additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "High",
      phon: "ok",
      decoding: "ok",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a high risk of future reading difficulty. The subtests show difficulty with word reading, but hearing and manipulating the sound structure of spoken language and connecting letters to their sounds in print are relative strengths.",
      recommendation: "Given the high risk categorization from the screener and the low word reading score, further evaluation and progress monitoring for reading difficulties is recommended. Additionally, the student would benefit from more opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Moderate",
      phon: "ok",
      decoding: "ok",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading difficulty. The subtests show difficulty with word reading, but hearing and manipulating the sound structure of spoken language and connecting letters to their sounds in print are relative strengths.",
      recommendation: "Given the moderate risk categorization from the screener and the low word reading score, further evaluation and progress monitoring of reading difficulties is recommended. Additionally, the student would benefit from additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Low",
      phon: "ok",
      decoding: "ok",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a low risk of future reading difficulty. The subtests show difficulty with word reading, but hearing and manipulating the sound structure of spoken language and connecting letters to their sounds in print are relative strengths.",
      recommendation: "Given the low risk categorization from the screener and the low word reading score, this student most likely needs more opportunities to build vocabulary through additional opportunities to practice reading connected texts."
    },
    {
      screening: "High",
      phon: "ok",
      decoding: "ok",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a high risk for future reading difficulty; however, the student is currently performing at grade-appropriate levels across all reading skills assessed.",
      recommendation: "The screener indicates that students with similar scores at this time of year are at high risk of struggling significantly with reading at the end of the school year. However, the subtest results show grade-appropriate performance on all subtests. It is recommended to consider observations and other assessments of reading to determine if concern is warranted. Additionally, like all students, the student would benefit from more opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Moderate",
      phon: "ok",
      decoding: "ok",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading difficulty. However, the student is performing at grade-appropriate levels in all tested reading skills.",
      recommendation: "The screener indicates that students with similar scores at this time of year are at moderate risk of struggling significantly with reading at the end of the school year. However, the subtest results show grade-appropriate performance on all subtests. It is recommended to consider observations and other assessments of reading to determine if concern is warranted. Additionally, like all students, the student would benefit from more opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Low",
      phon: "ok",
      decoding: "ok",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a low risk for future reading difficulty. The student is currently performing at grade-appropriate levels across all reading skills assessed.",
      recommendation: "Results indicate no concerns about the student's reading development at this time. To ensure continued grade-level reading skills, regular screening and progress monitoring are recommended. Like all students, the student would benefit from additional opportunities to practice reading connected texts to build reading vocabulary."
    }
  ],
  G3: [
    {
      screening: "High",
      decoding: "low",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a high probability of future reading difficulties. Consistent with this finding are the subtest results, which show difficulty connecting letters to their sounds in print and reading printed words. Together, these results suggest significant difficulties with reading.",
      recommendation: "Results from both the screener and subtests indicate a need for further evaluation and progress monitoring. In addition, targeted instruction in foundational reading skills is recommended, particularly in phonics, along with increased opportunities to practice reading connected text to support vocabulary development."
    },
    {
      screening: "Moderate",
      decoding: "low",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading challenges. The subtest results show difficulty connecting letters to their sounds in print and reading printed words. Together, these results suggest difficulties with reading.",
      recommendation: "Both the screener and subtests indicate the need for further evaluation and progress monitoring of reading difficulties. Also recommended are additional instruction in foundational reading skills, especially phonics, and additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Low",
      decoding: "low",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a low risk of future reading challenges. However, the subtest results show difficulty connecting letters to their sounds in print and reading printed words.",
      recommendation: "Although the screener indicates that the student is likely to be reading on grade-level at the end of the year, it is important to consider the low scores on the subtests as potentially indicating a risk of future reading difficulties. Further evaluation for reading difficulties is recommended. Also recommended is additional instruction in foundational reading skills, especially phonics, as well as additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "High",
      decoding: "low",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a high risk of future reading difficulty. In addition, the subtest results show difficulty connecting letters to their sounds in print. However, word reading ability is a relative strength.",
      recommendation: "The screener indicates that students with similar results struggled with future reading difficulties. Consider further evaluations and/or progress monitoring to ensure continued development. Also recommended are additional instruction in phonics and more opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Moderate",
      decoding: "low",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading difficulty. In addition, the subtest results show difficulty connecting letters to their sounds in print. However, word reading ability is a relative strength.",
      recommendation: "Taking the screening and subtest results as a whole, further evaluation and progress monitoring for reading difficulties is recommended. Also recommended are additional instruction in foundational reading skills, especially phonics, and additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Low",
      decoding: "low",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a low risk of future reading difficulty. However, the subtest results show difficulty connecting letters to their sounds in print. Yet, word reading ability is a relative strength, which is consistent with the low risk categorization.",
      recommendation: "Taking the screening and subtest results as a whole, it is recommended that this student 's development in phonics skills be closely monitored. Also recommended are additional instruction in foundational reading skills, especially phonics, and additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "High",
      decoding: "ok",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a high risk of future reading difficulty. The subtests show difficulty reading printed words. But the ability to connect letters to their sounds in print is a relative strength.",
      recommendation: "The screener indicates that students with similar results struggled with future reading difficulties. Taking the screening and subtest results as a whole, it is recommended that this student receive further evaluation and progress monitoring of reading difficulties. Also recommended are additional instruction in foundational reading skills and more opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Moderate",
      decoding: "ok",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading difficulty. The subtests show difficulty reading printed words. But the ability to connect letters to their sounds in print is a relative strength.",
      recommendation: "Taking the screening and subtest results as a whole, further evaluation and progress monitoring for reading difficulties is recommended. Also recommended are additional instruction in foundational reading skills and more opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Low",
      decoding: "ok",
      word: "low",
      interpretation: "The WRA screening algorithms indicate a low risk of future reading difficulty. The subtests show difficulty reading printed words. But the ability to connect letters to their sounds in print is a relative strength.",
      recommendation: "The screener suggests that this student will most likely test on grade level by the end of the year. However, low scores on the phonics task suggests this student would benefit from additional instruction and progress monitoring in foundational reading skills, especially phonics, as well as additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "High",
      decoding: "ok",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a high risk of future reading difficulty. However, the subtests indicate grade-level anility to connect letters to their sounds and read printed words.",
      recommendation: "The screener indicates that students with similar scores at this time of year are at a high risk of significantly struggling with reading at end of the school year. Although the subtests do not indicate problems, further evaluation and progress monitoring of reading difficulties is recommended. Given the screening results, this student would likely benefit from additional instruction in foundational reading skills and additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Moderate",
      decoding: "ok",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a moderate risk of future reading difficulty. However, the subtests indicate grade-level ability to connect letters to their sounds and read printed words.",
      recommendation: "The screener indicates that students with similar scores at this time of year are at a moderate risk of reading below grade level at end of the school year. Although the subtests do not indicate problems, further evaluation and progress monitoring of reading difficulties is recommended. Given the screening results, this student would likely benefit from additional instruction in foundational reading skills and additional opportunities to practice reading connected texts to build reading vocabulary."
    },
    {
      screening: "Low",
      decoding: "ok",
      word: "ok",
      interpretation: "The WRA screening algorithms indicate a low risk of future reading difficulty. The subtests indicate grade-level ability to connect letters to their sounds and read printed words.",
      recommendation: "Results indicate no concerns about the student's reading development at this time. To ensure continued grade-level reading skills, regular screening and progress monitoring are recommended. Like all students, the student would benefit from additional opportunities to practice reading connected texts to build reading vocabulary."
    }
  ]
} as const;

const NARRATIVE_SKILLS_TESTED = [
  {
    title: "Blending (Phonological Awareness)",
    body: "Blending measures the ability to blend syllables and sounds to make words — for example, combining the sounds made by the letters “p”, “e”, “t” to say the word “pet”. This skill, called phonological awareness, is one of the most important building blocks for learning to read."
  },
  {
    title: "Deletion (Phonological Awareness)",
    body: "Deletion measures the ability to hear and manipulate the sounds within spoken words — for example, saying “cat” and then being asked what word is left if you remove the /k/ sound. This skill, called phonological awareness, is one of the most important building blocks for learning to read."
  },
  {
    title: "Nonword Reading (Decoding)",
    body: "Nonword reading asks the child to read made-up words (like “blift” or “droam”) that follow the rules of English spelling. Because these words don’t exist in memory, children must use their phonics knowledge to sound them out. This task measures decoding — a core reading skill."
  },
  {
    title: "Word Reading",
    body: "Word reading measures the ability to recognize real words, drawing on both phonics knowledge and memory for words they have seen before. This subtest is the most direct measure of overall word reading ability and is the primary basis for the screening result."
  }
] as const;

const NARRATIVE_SCORE_DESCRIPTIONS = [
  {
    title: "Scale Score",
    body: "The Scale Score is an equal-interval, vertical scale that spans grades. It is an absolute measure of ability and can be used to compare performance over time. A student’s scale score can be expected to increase from one assessment to the next, provided sufficient time has elapsed between test administrations (at least one month)."
  },
  {
    title: "Standard Score",
    body: "The Standard Score tells you how a student performed compared to other students in the same grade and norming period, where the average is 100 and standard deviation is 15. Scores of 85 or below suggest performance below what is typically expected for that grade."
  },
  {
    title: "Percentile Rank",
    body: "The Percentile Rank measure shows how a student performed compared to a nationally representative norming sample. In general, scoring above the 40th percentile is considered on grade level, and scoring below the 16th percentile is considered severe risk. Students scoring between the 16th and 40th percentile are in the low-average range for their grade for the skill."
  },
  {
    title: "95% Confidence Intervals",
    body: "A test score isn't a perfect measurement — it's an estimate. If your child took the same test on different days, the score would vary slightly each time. A 95% confidence interval shows the range where your student’s true ability most likely falls. For example, a score of 87 with an interval of 77–96 means we're highly confident the true score is somewhere in that range."
  }
] as const;

const NARRATIVE_NOTE_ABOUT_ASSESSMENT = "As with all assessments, WRA results should be considered alongside other information about your child — including classroom performance, teacher observations, and your own knowledge of your child as a learner. A single assessment is never the full picture.";
const PERSON_MARKER_SYMBOL = `url(data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="28" viewBox="0 0 24 28" fill="none">
    <circle cx="12" cy="6" r="4" fill="#2f69a2"/>
    <path d="M12 11C8.13401 11 5 14.134 5 18V25C5 25.5523 5.44772 26 6 26H18C18.5523 26 19 25.5523 19 25V18C19 14.134 15.866 11 12 11Z" fill="#2f69a2"/>
  </svg>`
)})`;

const EXPORT_SECTION_TITLE_COLOR = '1f1f1f';
const EXPORT_BODY_COLOR = '22252b';
const EXPORT_MUTED_COLOR = '4d5565';
const EXPORT_TABLE_HEADER = '26306C';
const EXPORT_TABLE_ALT_ROW = 'eef5fd';

function sanitizeFileNamePart(value: string) {
  return value.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
}

function dataUrlToUint8Array(dataUrl: string) {
  const base64 = dataUrl.split(',')[1] || '';
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
}

async function fileToDataUrl(src: string) {
  const response = await fetch(src);
  const blob = await response.blob();

  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

async function fileToUint8Array(src: string) {
  const response = await fetch(src);
  const buffer = await response.arrayBuffer();
  return new Uint8Array(buffer);
}

function downloadBlob(blob: Blob, fileName: string) {
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(objectUrl);
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function exportValue(value: any) {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  return String(value);
}

function buildPdfTable(widths: Array<string | number>, header: string[], rows: string[][]) {
  return {
    table: {
      headerRows: 1,
      widths,
      body: [
        header.map((cell) => ({
          text: cell,
          fillColor: `#${EXPORT_TABLE_HEADER}`,
          color: '#ffffff',
          bold: true,
          fontSize: 8,
          margin: [3, 6, 3, 6]
        })),
        ...rows.map((row, rowIndex) => row.map((cell) => ({
          text: cell,
          fillColor: rowIndex % 2 === 1 ? `#${EXPORT_TABLE_ALT_ROW}` : '#ffffff',
          color: `#${EXPORT_BODY_COLOR}`,
          fontSize: 9,
          margin: [6, 6, 6, 6]
        })))
      ]
    },
    layout: {
      hLineColor: () => '#d7dbe3',
      vLineColor: () => '#d7dbe3',
      hLineWidth: () => 1,
      vLineWidth: () => 1
    },
    margin: [0, 14, 0, 0]
  };
}

function getPdfImageBlock(image: string, fit: [number, number]) {
  return {
    image,
    fit,
    alignment: 'center',
    margin: [0, 0, 0, 10]
  };
}

const CHART_EXPORT_CAPTURE_STYLE = {
  paddingTop: '10px',
  paddingRight: '18px',
  paddingBottom: '42px',
  paddingLeft: '18px',
  overflow: 'visible',
  backgroundColor: '#ffffff'
};

function getPdfTestTableWidths(columns: ExportTableColumn[], isSingleStudent: boolean) {
  if (isSingleStudent) {
    return columns.map((column) => {
      switch (column.key) {
        case 'completed_timestamp':
          return 54;
        case 'subtest':
          return 64;
        case 'norms':
          return 36;
        case 'scaleScore':
          return 48;
        case 'scaleScoreCI':
          return 50;
        case 'standardScore':
          return 50;
        case 'standardScoreCI':
          return 50;
        case 'percentileRank':
          return 42;
        case 'percentileRankCI':
          return 50;
        case 'flag':
          return 58;
        default:
          return '*';
      }
    });
  }

  return columns.map((column) => {
    switch (column.key) {
      case 'completed_timestamp':
        return 42;
      case 'student_name':
        return 68;
      case 'subtest':
        return 50;
      case 'norms':
        return 34;
      case 'scaleScore':
        return 44;
      case 'scaleScoreCI':
        return 44;
      case 'standardScore':
        return 44;
      case 'standardScoreCI':
        return 44;
      case 'percentileRank':
        return 32;
      case 'percentileRankCI':
        return 44;
      case 'flag':
        return 54;
      default:
        return '*';
    }
  });
}

function SearchableMultiSelect({
  options,
  values,
  onChange,
  ariaLabel,
  placeholder,
  singularLabel,
  pluralLabel,
  disabled = false
}: {
  options: SearchOption[];
  values: string[];
  onChange: (values: string[]) => void;
  ariaLabel: string;
  placeholder: string;
  singularLabel: string;
  pluralLabel: string;
  disabled?: boolean;
}) {
  const generatedId = useId().replace(/:/g, "");
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState("");
  const [localValues, setLocalValues] = useState(values);

  useEffect(() => {
    if (!show) {
      setSearch("");
    }
  }, [show]);

  useEffect(() => {
    setLocalValues(values);
  }, [values]);

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return options;
    }

    return options.filter((option) => option.label.toLowerCase().includes(normalizedSearch));
  }, [options, search]);

  const toggleValue = (value: string) => {
    const nextValues = localValues.includes(value)
      ? localValues.filter((currentValue) => currentValue !== value)
      : [...localValues, value];

    setLocalValues(nextValues);
    onChange(nextValues);
  };

  const selectedLabel = localValues.length === 0
    ? placeholder
    : localValues.length === 1
      ? `${options.find((option) => String(option.value) === localValues[0])?.label || singularLabel}`
      : `${localValues.length} ${pluralLabel} Selected`;

  return (
    <Dropdown show={show} onToggle={(nextShow) => setShow(!!nextShow)} className="searchable-select">
      <Dropdown.Toggle
        id={`searchable-multi-select-${generatedId}`}
        aria-label={ariaLabel}
        className={`searchable-select-toggle ${disabled ? 'searchable-select-toggle-disabled' : ''}`}
        disabled={disabled}
      >
        <span className={`searchable-select-label ${localValues.length === 0 ? 'searchable-select-label-placeholder' : ''}`}>
          {selectedLabel}
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu className="searchable-select-menu">
        <div className="searchable-select-search-wrapper" onClick={(event) => event.stopPropagation()}>
          <Form.Control
            type="search"
            placeholder="Search..."
            aria-label={`${ariaLabel} search`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            autoFocus
          />
        </div>
        <div className="searchable-select-options searchable-multi-select-options">
          {filteredOptions.map((option) => {
            const optionValue = String(option.value);
            const checked = localValues.includes(optionValue);

            return (
              <button
                key={optionValue}
                type="button"
                aria-pressed={checked}
                className={`dropdown-item searchable-multi-select-item ${checked ? 'active' : ''}`}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  toggleValue(optionValue);
                }}
              >
                <span className={`searchable-multi-select-checkbox ${checked ? 'searchable-multi-select-checkbox-checked' : ''}`}>
                  {checked ? '✓' : ''}
                </span>
                <span className="searchable-multi-select-item-label">{option.label}</span>
              </button>
            );
          })}
          {filteredOptions.length === 0 ? (
            <div className="searchable-select-empty">No matches found.</div>
          ) : null}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}

function ClinicianResults() {
  const terms = getUseCaseTerminology();
  const hierarchySelectionHandled = useRef(false);
  const directTestSelectionHandled = useRef(false);
  const [students, setStudents] = useState([] as any[]);
  const [classes, setClasses] = useState([] as any[]);
  const [groups, setGroups] = useState([] as any[]);
  const [categories, setCategories] = useState([] as any[]);
  const [loadingReferenceData, setLoadingReferenceData] = useState(true);
  const [loadingResults, setLoadingResults] = useState(false);
  const [error, setError] = useState("");

  const [selectedStudentIds, setSelectedStudentIds] = useState([] as string[]);
  const [selectedClassId, setSelectedClassId] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [selectedTestId, setSelectedTestId] = useState("");
  const [timeframe, setTimeframe] = useState<"recent" | "ytd" | "range">("recent");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [reportRequest, setReportRequest] = useState(null as ReportRequest | null);
  const [generatedCriteriaSignature, setGeneratedCriteriaSignature] = useState("");
  const [rawTests, setRawTests] = useState([] as any[]);
  const [showTableFilters, setShowTableFilters] = useState(false);
  const [subtestFilter, setSubtestFilter] = useState("All");
  const [normsFilter, setNormsFilter] = useState("All");
  const [testDateFilter, setTestDateFilter] = useState("All");
  const [progressSubtest, setProgressSubtest] = useState("");
  const [distributionSubtest, setDistributionSubtest] = useState("");
  const [averageSubtest, setAverageSubtest] = useState("");
  const [aggregateDisplayBy, setAggregateDisplayBy] = useState<"norming" | "month">("norming");
  const [openNarrativeSections, setOpenNarrativeSections] = useState([] as string[]);
  const [exportingFormat, setExportingFormat] = useState<"" | "pdf" | "word">("");
  const [exportingFamilyFormat, setExportingFamilyFormat] = useState<"" | "pdf" | "word">("");

  const screeningExportRef = useRef<HTMLDivElement | null>(null);
  const aggregateScreeningStripRef = useRef<HTMLDivElement | null>(null);
  const aggregateScreeningChartPanelRef = useRef<HTMLDivElement | null>(null);
  const riskHistoryChartPanelRef = useRef<HTMLDivElement | null>(null);
  const progressChartPanelRef = useRef<HTMLDivElement | null>(null);
  const percentileDistributionChartPanelRef = useRef<HTMLDivElement | null>(null);
  const averageScaleScoreChartPanelRef = useRef<HTMLDivElement | null>(null);
  const aggregateScreeningChartRef = useRef<any>(null);
  const riskHistoryChartRef = useRef<any>(null);
  const progressChartRef = useRef<any>(null);
  const percentileDistributionChartRef = useRef<any>(null);
  const averageScaleScoreChartRef = useRef<any>(null);

  const token = sessionStorage.getItem("token");

  useEffect(() => {
    const schoolYearStart = getSchoolYearStart();
    setStartDate(schoolYearStart);
    setEndDate(getTodayDateString());
  }, []);

  useEffect(() => {
    if (loadingReferenceData || hierarchySelectionHandled.current) {
      return;
    }

    hierarchySelectionHandled.current = true;
    const params = new URLSearchParams(window.location.search);
    const classId = params.get("class");
    const testId = params.get("test") || "";
    const studentIds = (params.get("students") || "")
      .split(",")
      .map((value) => value.trim())
      .filter((value) => value && students.some((student) => String(student.id) === value));

    if (classId && classes.some((classroom) => String(classroom.id) === classId)) {
      setSelectedClassId(classId);
      setSelectedStudentIds([]);
      setSelectedGroupId("");
    } else if (studentIds.length > 0) {
      setSelectedStudentIds(studentIds);
      setSelectedClassId("");
      setSelectedGroupId("");
      setSelectedTestId(testId);
    }
  }, [loadingReferenceData, students, classes]);

  useEffect(() => {
    let cancelled = false;

    const loadReferenceData = async () => {
      setLoadingReferenceData(true);

      try {
        const [studentsResponse, classesResponse, groupsResponse, schemasResponse] = await Promise.all([
          fetchApi('/api/students?page=1&sizePerPage=10000&sortField=last_name&sortDirection=asc&archived=false'),
          fetchApi('/api/classes?page=1&sizePerPage=1000&sortField=name&sortDirection=asc'),
          fetchApi('/api/groups?page=1&sizePerPage=1000&sortField=name&sortDirection=asc&archived=false'),
          fetchApi('/api/schemas')
        ]);

        if (cancelled) {
          return;
        }

        setStudents(studentsResponse.students || []);
        setClasses(classesResponse.classes || []);
        setGroups(groupsResponse.groups || []);
        setCategories(buildGroupedCategories(schemasResponse || []));
      } catch (loadError) {
        if (!cancelled) {
          setError("We couldn't load the data needed to build results right now.");
        }
      } finally {
        if (!cancelled) {
          setLoadingReferenceData(false);
        }
      }
    };

    loadReferenceData();

    return () => {
      cancelled = true;
    };
  }, []);

  const studentLookup = useMemo(() => {
    const map = new Map<string, any>();
    students.forEach((student) => {
      map.set(String(student.id), student);
    });
    return map;
  }, [students]);

  const studentOptions = useMemo(
    () => students.map((student) => ({
      value: String(student.id),
      label: `${student.first_name} ${student.last_name} (${student.student_code})`
    })),
    [students]
  );

  const classOptions = useMemo(
    () => classes.map((classroom) => ({
      value: String(classroom.id),
      label: `${classroom.name}${classroom.school_name ? ` - ${classroom.school_name}` : ''}`
    })),
    [classes]
  );

  const groupOptions = useMemo(
    () => groups.map((group) => ({
      value: String(group.id),
      label: group.name
    })),
    [groups]
  );

  const reportPopulationIsSingleStudent = reportRequest?.students.length === 1;

  const criteriaSignature = useMemo(() => JSON.stringify({
    studentIds: [...selectedStudentIds].sort(),
    classId: selectedClassId,
    groupId: selectedGroupId,
    timeframe,
    startDate: timeframe === 'range' ? startDate : '',
    endDate: timeframe === 'range' ? endDate : ''
  }), [selectedStudentIds, selectedClassId, selectedGroupId, timeframe, startDate, endDate]);

  const computedResults = useMemo(() => {
    if (!reportRequest || categories.length === 0 || rawTests.length === 0) {
      return {
        tests: [] as any[],
        allScoreRows: [] as any[],
      };
    }

    return computeWraResults(rawTests, categories, studentLookup);
  }, [reportRequest, rawTests, categories, studentLookup]);

  const availableNorms = useMemo(() => {
    return Array.from(new Set(computedResults.tests.map((test) => test.season).filter(Boolean))).sort();
  }, [computedResults.tests]);

  const availableTestDates = useMemo(() => {
    const dateMap = new Map<string, string>();

    computedResults.tests.forEach((test) => {
      const key = testDateKey(test.completed_timestamp);
      if (key && !dateMap.has(key)) {
        dateMap.set(key, formatDateLabel(test.completed_timestamp));
      }
    });

    return Array.from(dateMap.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [computedResults.tests]);

  const visibleScoreRows = useMemo(() => {
    return computedResults.allScoreRows.filter((row) => {
      if (subtestFilter !== "All" && row.subtest !== subtestFilter) {
        return false;
      }

      if (normsFilter !== "All" && row.norms !== normsFilter) {
        return false;
      }

      if (testDateFilter !== "All" && row.completed_date_key !== testDateFilter) {
        return false;
      }

      return true;
    });
  }, [computedResults.allScoreRows, subtestFilter, normsFilter, testDateFilter]);

  const availableSubtests = useMemo(() => {
    return Array.from(new Set(computedResults.allScoreRows.map((row) => row.subtest))).sort();
  }, [computedResults.allScoreRows]);

  useEffect(() => {
    if (subtestFilter !== "All" && !availableSubtests.includes(subtestFilter)) {
      setSubtestFilter("All");
    }
  }, [availableSubtests, subtestFilter]);

  useEffect(() => {
    if (normsFilter !== "All" && !availableNorms.includes(normsFilter)) {
      setNormsFilter("All");
    }
  }, [availableNorms, normsFilter]);

  useEffect(() => {
    if (testDateFilter !== "All" && !availableTestDates.some(([key]) => key === testDateFilter)) {
      setTestDateFilter("All");
    }
  }, [availableTestDates, testDateFilter]);

  const progressSubtestOptions = useMemo(() => {
    return Array.from(
      new Set(
        computedResults.allScoreRows
          .filter((row) => isNumericScoreValue(row.scaleScore) && isNumericScoreValue(row.percentileRank))
          .map((row) => row.subtest)
      )
    ).sort();
  }, [computedResults.allScoreRows]);

  useEffect(() => {
    if (progressSubtestOptions.length === 0) {
      setProgressSubtest("");
      return;
    }

    if (!progressSubtest || !progressSubtestOptions.includes(progressSubtest)) {
      setProgressSubtest(progressSubtestOptions[0]);
    }
  }, [progressSubtest, progressSubtestOptions]);

  useEffect(() => {
    if (availableSubtests.length === 0) {
      setDistributionSubtest("");
      setAverageSubtest("");
      return;
    }

    if (!distributionSubtest || !availableSubtests.includes(distributionSubtest)) {
      setDistributionSubtest(availableSubtests[0]);
    }

    if (!averageSubtest || !availableSubtests.includes(averageSubtest)) {
      setAverageSubtest(availableSubtests[0]);
    }
  }, [availableSubtests, distributionSubtest, averageSubtest]);

  useEffect(() => {
    setOpenNarrativeSections([]);
  }, [reportRequest, rawTests]);

  const computedTestRisks = useMemo(() => {
    const map = new Map<string, "High" | "Moderate" | "Low" | null>();

    computedResults.tests.forEach((test) => {
      map.set(
        String(test.id),
        resolveTestRiskClassification(test, computedResults.allScoreRows, studentLookup)
      );
    });

    return map;
  }, [computedResults.tests, computedResults.allScoreRows, studentLookup]);

  const aggregateUniqueScreeningDates = useMemo(() => {
    return Array.from(
      new Set(
        computedResults.tests
          .map((test) => testDateKey(test.completed_timestamp))
          .filter(Boolean)
      )
    ).sort();
  }, [computedResults.tests]);

  const screeningSelectedTest = useMemo(() => {
    return computedResults.tests
      .slice()
      .sort((a, b) => new Date(b.completed_timestamp).getTime() - new Date(a.completed_timestamp).getTime())[0];
  }, [computedResults.tests]);

  const screeningScoreRow = useMemo(() => {
    if (!screeningSelectedTest) {
      return null;
    }

    return computedResults.allScoreRows.find((row) =>
      row.testId?.toString() === screeningSelectedTest.id?.toString()
      && row.subtest === "Word Reading"
      && isNumericScoreValue(row.scaleScore)
    ) || null;
  }, [screeningSelectedTest, computedResults.allScoreRows]);

  const screeningStudent = screeningSelectedTest ? resolveStudentInfo(screeningSelectedTest, studentLookup) : null;
  const screeningGrade = normalizeGrade(screeningStudent?.grade || "");
  const screeningConfig = screeningSelectedTest ? SCREENING_RISK_CUTPOINTS[`${screeningGrade}|${screeningSelectedTest.season}`] : null;
  const screeningResolvedRiskCategory = screeningSelectedTest
    ? computedTestRisks.get(String(screeningSelectedTest.id)) || null
    : null;
  const screeningScaleScore = screeningScoreRow && isNumericScoreValue(screeningScoreRow.scaleScore)
    ? Number(screeningScoreRow.scaleScore)
    : null;
  const screeningNarrativeOnly = !!screeningSelectedTest && screeningGrade === "K" && (screeningSelectedTest.season === "Fall" || screeningSelectedTest.season === "Winter");
  const screeningDisplayRiskCategory = screeningResolvedRiskCategory;
  const screeningRiskContent = screeningDisplayRiskCategory ? SCREENING_RISK_CONTENT[screeningDisplayRiskCategory] : null;
  const screeningStudentFullName = screeningStudent
    ? `${screeningStudent.first_name || ''} ${screeningStudent.last_name || ''}`.trim()
    : 'This student';
  const screeningAxisMin = screeningConfig?.lowerBound || 200;
  const screeningAxisMax = screeningConfig?.upperBound || 1000;
  const screeningRange = screeningAxisMax - screeningAxisMin;
  const screeningHighWidth = screeningConfig ? ((screeningConfig.highRiskCutPoint - screeningAxisMin) / screeningRange) * 100 : 0;
  const screeningModerateWidth = screeningConfig ? ((screeningConfig.moderateRiskCutPoint - screeningConfig.highRiskCutPoint) / screeningRange) * 100 : 0;
  const screeningLowWidth = screeningConfig ? ((screeningAxisMax - screeningConfig.moderateRiskCutPoint) / screeningRange) * 100 : 0;
  const screeningMarkerLeft = screeningScaleScore !== null
    ? Math.min(100, Math.max(0, ((screeningScaleScore - screeningAxisMin) / screeningRange) * 100))
    : 0;
  const screeningMarkerEdgeClass = screeningMarkerLeft <= 12
    ? 'results-screening-band-boundary-center-edge-left'
    : screeningMarkerLeft >= 88
      ? 'results-screening-band-boundary-center-edge-right'
      : '';
  const screeningMostRecentDateLabel = screeningSelectedTest ? formatDateLabel(screeningSelectedTest.completed_timestamp) : "";
  const isSingleStudentMultipleTests = reportPopulationIsSingleStudent && computedResults.tests.length > 1;
  const isAggregateRecentView = !reportPopulationIsSingleStudent && aggregateUniqueScreeningDates.length === 1;
  const isAggregateMultiTestView = !reportPopulationIsSingleStudent && aggregateUniqueScreeningDates.length > 1;

  const singleStudentRiskHistoryOptions = useMemo(() => {
    if (!isSingleStudentMultipleTests) {
      return null;
    }

    const rows = computedResults.tests
      .map((test) => ({
        ...test,
        normalizedClassification: computedTestRisks.get(String(test.id)) || null
      }))
      .filter((test) => !!test.normalizedClassification)
      .sort((a, b) => new Date(a.completed_timestamp).getTime() - new Date(b.completed_timestamp).getTime());

    if (rows.length === 0) {
      return null;
    }

    const categoryMap: Record<string, number> = {
      'Low': 0,
      'Moderate': 1,
      'High': 2
    };

    return {
      chart: {
        type: 'line',
        height: 280,
        backgroundColor: '#ffffff'
      },
      title: {
        text: 'Risk Over Time'
      },
      credits: {
        enabled: false
      },
      xAxis: {
        categories: rows.map((test) => formatDateLabel(test.completed_timestamp))
      },
      yAxis: {
        title: {
          text: ''
        },
        min: -0.5,
        max: 2.5,
        categories: ['Low Risk', 'Moderate Risk', 'High Risk']
      },
      legend: {
        enabled: false
      },
      tooltip: {
        pointFormatter: function () {
          return `<strong>${(this as any).customCategory}</strong><br/>${(this as any).customDate}`;
        }
      },
      plotOptions: {
        line: {
          lineWidth: 0,
          marker: {
            enabled: true,
            radius: 10
          },
          dataLabels: {
            enabled: true,
            formatter: function (): string {
              const category = ((this as any).point as any).customClassification;
              if (category === 'High') {
                return '↑';
              }
              if (category === 'Moderate') {
                return '–';
              }
              return '↓';
            },
            style: {
              color: '#ffffff',
              fontWeight: '700',
              textOutline: 'none'
            }
          }
        }
      },
      series: [
        {
          type: 'line',
          name: 'Risk classification',
          data: rows.map((test, index) => ({
            x: index,
            y: categoryMap[test.normalizedClassification || 'Low'] ?? 0,
            color: test.normalizedClassification === 'High'
              ? SCREENING_RISK_CONTENT.High.graphColor
              : test.normalizedClassification === 'Moderate'
                ? SCREENING_RISK_CONTENT.Moderate.graphColor
                : SCREENING_RISK_CONTENT.Low.graphColor,
            customClassification: test.normalizedClassification,
            customCategory: `${test.normalizedClassification} Risk`,
            customDate: formatDateLabel(test.completed_timestamp)
          }))
        }
      ]
    };
  }, [computedResults.tests, computedTestRisks, isSingleStudentMultipleTests]);

  const progressRows = useMemo(() => {
    return computedResults.allScoreRows
      .filter((row) => row.subtest === progressSubtest)
      .filter((row) => isNumericScoreValue(row.scaleScore) && isNumericScoreValue(row.percentileRank))
      .sort((a, b) => {
        const dateCompare = a.completed_date_key.localeCompare(b.completed_date_key);
        if (dateCompare !== 0) {
          return dateCompare;
        }
        return a.testId - b.testId;
      });
  }, [computedResults.allScoreRows, progressSubtest]);

  const buildProgressChartOptionsForRows = (rows: any[], subtest: string) => {
    return {
      chart: {
        zoomType: 'x',
        height: 460,
        spacingBottom: 28
      },
      title: {
        text: subtest ? `Progress Over Time - ${subtest}` : 'Progress Over Time'
      },
      credits: {
        enabled: false
      },
      xAxis: {
        categories: rows.map((row) => formatDateLabel(row.completed_timestamp)),
        crosshair: true,
        labels: {
          reserveSpace: true,
          style: {
            fontSize: '11px'
          }
        }
      },
      yAxis: [
        {
          title: {
            text: 'Scale Score'
          },
          min: 200,
          max: 1000
        },
        {
          title: {
            text: 'Percentile Rank'
          },
          min: 0,
          max: 100,
          opposite: true
        }
      ],
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        y: 8
      },
      tooltip: {
        shared: true
      },
      plotOptions: {
        column: {
          borderWidth: 1,
          pointPadding: 0.15,
          groupPadding: 0.2
        },
        spline: {
          lineWidth: 3,
          marker: {
            radius: 4,
            fillColor: '#111111',
            lineColor: '#111111',
            lineWidth: 1
          }
        },
        series: {
          animation: false,
          dataLabels: {
            enabled: true,
            style: {
              fontSize: '12px',
              textOutline: 'none'
            }
          }
        }
      },
      series: [
        {
          type: 'column',
          name: 'Percentile Rank',
          yAxis: 1,
          data: rows.map((row) => Number(row.percentileRank)),
          color: '#c8d7ea',
          borderColor: '#6f93c5'
        },
        {
          type: 'spline',
          name: 'Scale Score',
          yAxis: 0,
          data: rows.map((row) => Number(row.scaleScore)),
          color: '#111111',
          zIndex: 3
        }
      ]
    };
  };

  const progressChartOptions = useMemo(() => {
    if (!isSingleStudentMultipleTests || progressRows.length < 2) {
      return null;
    }

    return buildProgressChartOptionsForRows(progressRows, progressSubtest);
  }, [isSingleStudentMultipleTests, progressRows, progressSubtest]);

  const aggregateLatestTests = useMemo(() => {
    return latestTestPerStudent(computedResults.tests);
  }, [computedResults.tests]);

  const aggregateRiskSummary = useMemo(() => {
    const counts = {
      High: 0,
      Moderate: 0,
      Low: 0
    };

    aggregateLatestTests.forEach((test) => {
      const classification = computedTestRisks.get(String(test.id)) || null;

      if (classification === 'High') {
        counts.High += 1;
      } else if (classification === 'Moderate') {
        counts.Moderate += 1;
      } else if (classification === 'Low') {
        counts.Low += 1;
      }
    });

    return counts;
  }, [aggregateLatestTests, computedTestRisks]);

  const aggregateRiskTotal = aggregateLatestTests.length;

  const aggregateRiskRows = useMemo(() => {
    return aggregateLatestTests.map((test) => {
      const currentStudent = resolveStudentInfo(test, studentLookup);
      const classification = computedTestRisks.get(String(test.id)) || null;

      return {
        id: test.id,
        student_name: `${currentStudent?.first_name || test.student_first_name} ${currentStudent?.last_name || test.student_last_name}`.trim(),
        completed_timestamp: test.completed_timestamp,
        risk_category: classification
          ? `${classification} Risk`
          : 'N/A'
      };
    });
  }, [aggregateLatestTests, computedTestRisks, studentLookup]);

  const aggregateRiskColumns = [
    {
      dataField: 'student_name',
      text: terms.student,
      sort: true
    },
    {
      dataField: 'completed_timestamp',
      text: 'Test Date',
      sort: true,
      formatter: (cell: any) => formatDateLabel(cell)
    },
    {
      dataField: 'risk_category',
      text: 'Risk Category',
      sort: true,
      formatter: (cell: any) => (
        <div className="results-risk-pill-row">
          <span className={`results-risk-pill results-risk-pill-${(cell || '').toLowerCase().replace(/\s+/g, '-')}`}>{cell}</span>
        </div>
      )
    }
  ];

  const aggregateScreeningChartOptions = useMemo(() => {
    if (!isAggregateMultiTestView) {
      return null;
    }

    const intervalLabels = buildAggregateIntervalLabels(computedResults.tests, aggregateDisplayBy);
    if (intervalLabels.length === 0) {
      return null;
    }

    const countsByInterval: Record<string, { High: number; Moderate: number; Low: number }> = {};
    intervalLabels.forEach((label) => {
      countsByInterval[label] = { High: 0, Moderate: 0, Low: 0 };
    });

    const latestByStudentAndInterval = latestTestsPerStudentAndInterval(computedResults.tests, aggregateDisplayBy);
    latestByStudentAndInterval.forEach((test) => {
      const intervalLabel = aggregateIntervalLabelForTest(test, aggregateDisplayBy);
      const classification = computedTestRisks.get(String(test.id)) || null;

      if (!intervalLabel || !countsByInterval[intervalLabel] || !classification) {
        return;
      }

      if (classification === 'High') {
        countsByInterval[intervalLabel].High += 1;
      } else if (classification === 'Moderate') {
        countsByInterval[intervalLabel].Moderate += 1;
      } else if (classification === 'Low') {
        countsByInterval[intervalLabel].Low += 1;
      }
    });

    const hasData = intervalLabels.some((label) => {
      return countsByInterval[label].High + countsByInterval[label].Moderate + countsByInterval[label].Low > 0;
    });

    if (!hasData) {
      return null;
    }

    return {
      chart: {
        type: 'column',
        height: 420,
        backgroundColor: '#ffffff'
      },
      title: {
        text: aggregateDisplayBy === 'month'
          ? 'Students by Risk Category by Month'
          : 'Students by Risk Category at Each Norming Period',
        style: {
          color: '#22252b',
          fontSize: '20px',
          fontWeight: '700'
        }
      },
      credits: {
        enabled: false
      },
      xAxis: {
        categories: intervalLabels,
        lineWidth: 0,
        tickWidth: 0,
        labels: {
          style: {
            color: '#22252b',
            fontSize: '14px',
            fontWeight: '500'
          }
        }
      },
      yAxis: {
        min: 0,
        title: {
          text: null
        },
        gridLineColor: '#d7dbe3',
        labels: {
          style: {
            color: '#4d5565'
          }
        },
        stackLabels: {
          enabled: false
        }
      },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          color: '#22252b',
          fontWeight: '700'
        }
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            formatter: function (): string {
              const y = (this as any).y;
              return y ? `${y}` : '';
            },
            style: {
              color: '#ffffff',
              textOutline: 'none',
              fontWeight: '700',
              fontSize: '16px'
            }
          }
        }
      },
      series: [
        {
          name: 'High Risk',
          data: intervalLabels.map((label) => countsByInterval[label].High),
          color: SCREENING_RISK_CONTENT.High.graphColor
        },
        {
          name: 'Moderate Risk',
          data: intervalLabels.map((label) => countsByInterval[label].Moderate),
          color: SCREENING_RISK_CONTENT.Moderate.graphColor
        },
        {
          name: 'Low Risk',
          data: intervalLabels.map((label) => countsByInterval[label].Low),
          color: SCREENING_RISK_CONTENT.Low.graphColor
        }
      ]
    };
  }, [aggregateDisplayBy, computedResults.tests, computedTestRisks, isAggregateMultiTestView]);

  const percentileDistributionChartOptions = useMemo(() => {
    if (!isAggregateRecentView || !distributionSubtest) {
      return null;
    }

    const latestRowsByStudent = latestScoreRowsForSubtest(computedResults.allScoreRows, distributionSubtest);
    if (latestRowsByStudent.length === 0) {
      return null;
    }

    return {
      chart: {
        type: 'scatter',
        height: 520,
        backgroundColor: '#ffffff'
      },
      title: {
        text: `National Percentile Rank Distribution - ${distributionSubtest}`
      },
      credits: {
        enabled: false
      },
      xAxis: {
        categories: latestRowsByStudent.map((row) => row.student_short_label || row.student_name || `S${row.student_id}`),
        lineWidth: 0,
        tickWidth: 0,
        title: {
          text: 'Student'
        },
        labels: {
          style: {
            color: '#22252b',
            fontSize: '14px',
            fontWeight: '500'
          }
        }
      },
      yAxis: {
        min: 0,
        max: 100,
        title: {
          text: 'National Percentile Rank'
        },
        gridLineColor: '#d7dbe3',
        labels: {
          style: {
            color: '#4d5565'
          }
        }
      },
      legend: {
        enabled: false
      },
      tooltip: {
        pointFormatter: function () {
          return `<strong>${(this as any).studentName}</strong><br/>Percentile Rank: ${(this as any).y}`;
        }
      },
      series: [
        {
          type: 'scatter',
          name: 'National percentile rank',
          color: MODERATE_THEME,
          marker: {
            enabled: true,
            symbol: PERSON_MARKER_SYMBOL,
            width: 20,
            height: 24
          },
          data: latestRowsByStudent.map((row, index) => ({
            x: index,
            y: Number(row.percentileRank),
            studentName: row.student_name
          }))
        }
      ]
    };
  }, [computedResults.allScoreRows, distributionSubtest, isAggregateRecentView]);

  const averageScaleScoreChartOptions = useMemo(() => {
    if (!isAggregateMultiTestView || !averageSubtest) {
      return null;
    }

    const intervalLabels = buildAggregateIntervalLabels(computedResults.tests, aggregateDisplayBy);
    if (intervalLabels.length === 0) {
      return null;
    }

    const rowsByInterval = latestScoreRowsForSubtestAndInterval(computedResults.allScoreRows, averageSubtest, aggregateDisplayBy);
    const averages = intervalLabels.map((label) => {
      const intervalRows = rowsByInterval.filter((row) => aggregateIntervalLabelForRow(row, aggregateDisplayBy) === label);
      if (intervalRows.length === 0) {
        return null;
      }

      const total = intervalRows.reduce((sum, row) => sum + Number(row.scaleScore), 0);
      return Math.round(total / intervalRows.length);
    });

    if (averages.every((value) => value === null)) {
      return null;
    }

    return {
      chart: {
        type: 'column',
        height: 520,
        backgroundColor: '#ffffff'
      },
      title: {
        text: `Average Scale Score: ${averageSubtest}`,
        style: {
          color: '#22252b',
          fontSize: '20px',
          fontWeight: '700'
        }
      },
      credits: {
        enabled: false
      },
      xAxis: {
        categories: intervalLabels,
        lineWidth: 0,
        tickWidth: 0,
        labels: {
          style: {
            color: '#22252b',
            fontSize: '14px',
            fontWeight: '500'
          }
        }
      },
      yAxis: {
        min: 0,
        title: {
          text: null
        },
        gridLineColor: '#d7dbe3',
        labels: {
          style: {
            color: '#4d5565'
          }
        }
      },
      legend: {
        enabled: false
      },
      plotOptions: {
        column: {
          color: BLUE_THEME,
          borderWidth: 0,
          dataLabels: {
            enabled: true,
            formatter: function (): string {
              const y = (this as any).y;
              return y ? `${y}` : '';
            },
            crop: false,
            overflow: 'allow',
            inside: true,
            verticalAlign: 'middle',
            style: {
              color: '#ffffff',
              textOutline: 'none',
              fontWeight: '700',
              fontSize: '28px'
            }
          },
          pointPadding: 0.18,
          groupPadding: 0.12
        }
      },
      tooltip: {
        pointFormatter: function () {
          return `<strong>${(this as any).category}</strong><br/>Average Scale Score: ${(this as any).y}`;
        }
      },
      series: [
        {
          type: 'column',
          name: 'Average scale score',
          data: averages.map((value, index) => ({
            y: value,
            category: intervalLabels[index]
          }))
        }
      ]
    };
  }, [aggregateDisplayBy, computedResults.allScoreRows, computedResults.tests, averageSubtest, isAggregateMultiTestView]);

  const testResultsColumns = useMemo(() => {
    const baseColumns = [
      {
        dataField: 'completed_timestamp',
        text: 'Test Date',
        sort: true,
        formatter: (cell: any) => formatDateLabel(cell)
      }
    ] as any[];

    if (!reportPopulationIsSingleStudent) {
      baseColumns.push({
        dataField: 'student_name',
        text: 'Student',
        sort: true
      });
    }

    return [
      ...baseColumns,
      {
        dataField: 'subtest',
        text: 'Subtest',
        sort: true
      },
      {
        dataField: 'norms',
        text: 'Norms',
        sort: true
      },
      {
        dataField: 'scaleScore',
        text: 'Scale Score',
        sort: true
      },
      {
        dataField: 'scaleScoreCI',
        text: '95% CI'
      },
      {
        dataField: 'standardScore',
        text: 'Standard Score',
        sort: true
      },
      {
        dataField: 'standardScoreCI',
        text: '95% CI'
      },
      {
        dataField: 'percentileRank',
        text: reportPopulationIsSingleStudent ? 'Percentile Rank' : '% Rank',
        sort: true
      },
      {
        dataField: 'percentileRankCI',
        text: '95% CI'
      },
      {
        dataField: 'flag',
        text: 'Floor/Ceiling Flag',
        sort: true,
        formatter: (cell: any) => cell || ''
      }
    ];
  }, [reportPopulationIsSingleStudent]);

  const hasGeneratedResults = !!reportRequest;
  const generatedResultsAreCurrent = hasGeneratedResults && generatedCriteriaSignature === criteriaSignature;

  const singleStudentInfo = reportRequest?.students[0] || null;
  const singleStudentNarrative = useMemo(() => {
    if (!reportPopulationIsSingleStudent || !screeningSelectedTest) {
      return null;
    }

    return resolveNarrativeProfile(screeningSelectedTest, computedResults.allScoreRows, studentLookup);
  }, [reportPopulationIsSingleStudent, screeningSelectedTest, computedResults.allScoreRows, studentLookup]);

  const narrativeSections = useMemo<NarrativeSectionData[]>(() => {
    if (!hasGeneratedResults) {
      return [];
    }

    const sections: NarrativeSectionData[] = [
      {
        id: 'skills-tested',
        title: 'The Skills Tested By WRA',
        blocks: NARRATIVE_SKILLS_TESTED.map((item) => ({
          title: item.title,
          body: item.body
        }))
      },
      {
        id: 'score-descriptions',
        title: 'Score Descriptions',
        blocks: NARRATIVE_SCORE_DESCRIPTIONS.map((item) => ({
          title: item.title,
          body: item.body
        }))
      }
    ];

    if (reportPopulationIsSingleStudent && singleStudentNarrative?.showProfileSections && singleStudentNarrative.profile) {
      sections.push({
        id: 'results-summary',
        title: 'Results Summary',
        blocks: [{ body: singleStudentNarrative.profile.interpretation }]
      });
      sections.push({
        id: 'recommendations',
        title: 'Recommendations',
        blocks: [{ body: singleStudentNarrative.profile.recommendation }]
      });
    }

    sections.push({
      id: 'note-about-assessment',
      title: 'A Note About This Assessment',
      blocks: [{ body: NARRATIVE_NOTE_ABOUT_ASSESSMENT }]
    });

    return sections;
  }, [hasGeneratedResults, reportPopulationIsSingleStudent, singleStudentNarrative]);

  const generateResults = async (specificTestId = "") => {
    if (!token) {
      return;
    }

    setError("");

    if (selectedStudentIds.length === 0 && !selectedClassId && !selectedGroupId) {
      setError("Please choose at least one student, class, or group.");
      return;
    }

    if (timeframe === 'range' && (!startDate || !endDate)) {
      setError("Please choose both a start date and an end date.");
      return;
    }

    setLoadingResults(true);

    try {
      let requestedStudents: any[] = [];
      let requestMode: "student" | "class" | "group" = "student";
      let populationLabel = "";

      if (selectedStudentIds.length > 0) {
        requestMode = "student";
        requestedStudents = students.filter((student) => selectedStudentIds.includes(String(student.id)));
        populationLabel = selectedStudentIds.length === 1
          ? `${requestedStudents[0]?.first_name || ''} ${requestedStudents[0]?.last_name || ''}`.trim()
          : `${selectedStudentIds.length} Students`;
      } else if (selectedClassId) {
        requestMode = "class";
        requestedStudents = await loadStudentsForClass(selectedClassId);
        populationLabel = classOptions.find((option) => String(option.value) === selectedClassId)?.label || "Selected Class";
      } else if (selectedGroupId) {
        requestMode = "group";
        requestedStudents = await loadStudentsForGroup(selectedGroupId);
        populationLabel = groupOptions.find((option) => String(option.value) === selectedGroupId)?.label || "Selected Group";
      }

      const requestedStudentIds = requestedStudents.map((student) => String(student.id));

      if (requestedStudentIds.length === 0) {
        setError("No students were found for the selected parameters.");
        setRawTests([]);
        setReportRequest({
          mode: requestMode,
          timeframe,
          populationLabel,
          studentIds: [],
          students: [],
          startDate,
          endDate
        });
        setGeneratedCriteriaSignature(criteriaSignature);
        return;
      }

      const testsResponse = await fetchApi(
        '/api/tests?page=1&sizePerPage=10000&sortField=completed_timestamp&sortDirection=desc&student='
        + requestedStudentIds.join('|')
        + '&completionStatus=Complete&archived=false&includeDetails=true&resultsOnly=true'
      );

      const filteredTests = specificTestId
        ? (testsResponse.tests || []).filter((test: any) => String(test.id) === String(specificTestId))
        : filterTestsForTimeframe(testsResponse.tests || [], timeframe, startDate, endDate);

      setSubtestFilter("All");
      setNormsFilter("All");
      setTestDateFilter("All");
      setShowTableFilters(false);
      setRawTests(filteredTests);
      setReportRequest({
        mode: requestMode,
        timeframe,
        populationLabel,
        studentIds: requestedStudentIds,
        students: requestedStudents,
        startDate,
        endDate
      });
      setGeneratedCriteriaSignature(criteriaSignature);
    } catch (generateError) {
      setError("We couldn't generate results right now. Please try again.");
    } finally {
      setLoadingResults(false);
    }
  };

  useEffect(() => {
    if (
      loadingReferenceData
      || directTestSelectionHandled.current
      || !selectedTestId
      || selectedStudentIds.length === 0
    ) {
      return;
    }

    directTestSelectionHandled.current = true;
    generateResults(selectedTestId);
  }, [loadingReferenceData, selectedStudentIds, selectedTestId]);

  async function fetchApi(path: string) {
    const response = await fetch(ventrisConfig.url + path, {
      method: 'get',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      }
    });

    return response.json();
  }

  async function loadStudentsForClass(classId: string) {
    const response = await fetchApi('/api/students?page=1&sizePerPage=10000&organization=' + classId + '&archived=false&sortField=last_name&sortDirection=asc');
    return response.students || [];
  }

  async function loadStudentsForGroup(groupId: string) {
    const response = await fetchApi('/api/students?page=1&sizePerPage=10000&group=' + groupId + '&archived=false&sortField=last_name&sortDirection=asc');
    return response.students || [];
  }

  const screeningExportCaption = useMemo(() => {
    if (!reportRequest) {
      return '';
    }

    if (reportRequest.timeframe === 'recent') {
      return 'Most Recent Screening';
    }

    if (reportRequest.timeframe === 'ytd') {
      return 'All Screenings: Current School Year to Date';
    }

    if (reportRequest.startDate && reportRequest.endDate) {
      return `Specific Date Range: ${formatDateLabel(reportRequest.startDate)} - ${formatDateLabel(reportRequest.endDate)}`;
    }

    return 'Specific Date Range';
  }, [reportRequest]);

  const exportFileBaseName = useMemo(() => {
    const base = reportRequest?.populationLabel || 'results';
    const timeframeLabel = reportRequest?.timeframe || 'report';
    return `ventris-results-${sanitizeFileNamePart(base)}-${sanitizeFileNamePart(timeframeLabel)}`;
  }, [reportRequest]);

  const familyHasMultipleTestDates = reportPopulationIsSingleStudent && availableTestDates.length > 1;

  const familyReportFileBaseName = useMemo(() => {
    const base = reportRequest?.populationLabel || 'student';
    const dateLabel = familyHasMultipleTestDates ? 'multiple-test-dates' : sanitizeFileNamePart(screeningMostRecentDateLabel || 'report');
    return `family-report-${sanitizeFileNamePart(base)}-${dateLabel}`;
  }, [reportRequest?.populationLabel, screeningMostRecentDateLabel, familyHasMultipleTestDates]);

  const visibleTestResultExportColumns = useMemo<ExportTableColumn[]>(() => {
    const columns: ExportTableColumn[] = [
      {
        key: 'completed_timestamp',
        label: 'Test Date'
      }
    ];

    if (!reportPopulationIsSingleStudent) {
      columns.push({
        key: 'student_name',
        label: terms.student
      });
    }

    columns.push(
      { key: 'subtest', label: 'Subtest' },
      { key: 'norms', label: 'Norms' },
      { key: 'scaleScore', label: 'Scale Score' },
      { key: 'scaleScoreCI', label: '95% CI' },
      { key: 'standardScore', label: 'Standard Score' },
      { key: 'standardScoreCI', label: '95% CI' },
      { key: 'percentileRank', label: reportPopulationIsSingleStudent ? 'Percentile Rank' : '% Rank' },
      { key: 'percentileRankCI', label: '95% CI' },
      { key: 'flag', label: 'Floor/Ceiling Flag' }
    );

    return columns;
  }, [reportPopulationIsSingleStudent, terms.student]);

  const visibleTestResultExportRows = useMemo(() => {
    return visibleScoreRows.map((row) => visibleTestResultExportColumns.map((column) => {
      if (column.key === 'completed_timestamp') {
        return formatDateLabel(row.completed_timestamp);
      }

      return exportValue((row as any)[column.key]);
    }));
  }, [visibleScoreRows, visibleTestResultExportColumns]);

  const aggregateRiskExportColumns = useMemo<ExportTableColumn[]>(() => ([
    { key: 'student_name', label: terms.student },
    { key: 'completed_timestamp', label: 'Test Date' },
    { key: 'risk_category', label: 'Risk Category' }
  ]), [terms.student]);

  const aggregateRiskExportRows = useMemo(() => {
    return aggregateRiskRows.map((row) => aggregateRiskExportColumns.map((column) => {
      if (column.key === 'completed_timestamp') {
        return formatDateLabel((row as any)[column.key]);
      }

      return exportValue((row as any)[column.key]);
    }));
  }, [aggregateRiskRows, aggregateRiskExportColumns]);

  const reportPopulationLabel = reportRequest?.populationLabel || 'Results';
  const reportModeLabel = reportRequest?.mode === 'class'
    ? terms.class
    : reportRequest?.mode === 'group'
      ? 'Group'
      : terms.student;
  const canGenerateFamilyReport = generatedResultsAreCurrent && reportPopulationIsSingleStudent;

  async function captureElementImage(
    ref: { current: HTMLDivElement | null },
    styleOverrides: Record<string, string> = {}
  ) {
    if (!ref.current) {
      return null;
    }
    const sourceNode = ref.current;
    const wrapper = document.createElement('div');
    const clone = sourceNode.cloneNode(true) as HTMLDivElement;
    const sourceRect = sourceNode.getBoundingClientRect();
    const sourceWidth = Math.ceil(sourceRect.width);
    const sourceHeight = Math.ceil(sourceRect.height);
    const horizontalPadding = 24;
    const verticalPadding = 24;
    const captureWidth = sourceWidth + horizontalPadding;
    const captureHeight = sourceHeight + verticalPadding;

    wrapper.style.position = 'fixed';
    wrapper.style.left = '-10000px';
    wrapper.style.top = '0';
    wrapper.style.width = `${captureWidth}px`;
    wrapper.style.height = `${captureHeight}px`;
    wrapper.style.padding = '12px';
    wrapper.style.boxSizing = 'border-box';
    wrapper.style.background = '#ffffff';
    wrapper.style.overflow = 'visible';

    clone.style.width = `${sourceWidth}px`;
    clone.style.maxWidth = 'none';
    clone.style.height = 'auto';
    clone.style.overflow = 'visible';
    Object.assign(clone.style, styleOverrides);

    wrapper.appendChild(clone);
    document.body.appendChild(wrapper);

    try {
      return await toPng(wrapper, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        width: captureWidth,
        height: captureHeight,
        canvasWidth: captureWidth * 2,
        canvasHeight: captureHeight * 2,
        style: {
          width: `${captureWidth}px`,
          height: `${captureHeight}px`,
          overflow: 'visible',
          backgroundColor: '#ffffff'
        }
      });
    } finally {
      document.body.removeChild(wrapper);
    }
  }

  async function captureHighchartsImage(
    chartRef: { current: any },
    options: { panelWidth?: number; panelHeight?: number; padding?: number } = {}
  ) {
    const chart = chartRef.current?.chart;
    if (!chart) {
      return null;
    }

    const padding = options.padding ?? 18;
    const sourceHeight = options.panelHeight ?? chart.chartHeight;
    const sourceWidth = options.panelWidth
      ?? Math.min(chart.chartWidth || 680, Math.max(680, Math.round(sourceHeight * 2.45)));
    const svgMarkup = typeof chart.getSVG === 'function'
      ? chart.getSVG({
        chart: {
          width: sourceWidth,
          height: sourceHeight
        }
      })
      : chart.container?.querySelector('svg')?.outerHTML;

    if (!svgMarkup) {
      return null;
    }

    const captureWidth = Math.ceil(sourceWidth + padding * 2);
    const captureHeight = Math.ceil(sourceHeight + padding * 2);
    const normalizedSvg = svgMarkup.includes('xmlns=')
      ? svgMarkup
      : svgMarkup.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
    const svgBlob = new Blob([normalizedSvg], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = window.URL.createObjectURL(svgBlob);
    const image = new Image();

    try {
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error('Unable to render Highcharts SVG for export.'));
        image.src = svgUrl;
      });

      const pixelRatio = 2;
      const canvas = document.createElement('canvas');
      canvas.width = captureWidth * pixelRatio;
      canvas.height = captureHeight * pixelRatio;

      const context = canvas.getContext('2d');
      if (!context) {
        return null;
      }

      context.scale(pixelRatio, pixelRatio);
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, captureWidth, captureHeight);
      context.strokeStyle = '#26306C';
      context.lineWidth = 2;
      context.beginPath();
      context.roundRect(1, 1, captureWidth - 2, captureHeight - 2, 20);
      context.stroke();
      context.drawImage(image, padding, padding, sourceWidth, sourceHeight);

      return canvas.toDataURL('image/png');
    } finally {
      window.URL.revokeObjectURL(svgUrl);
    }
  }

  async function captureHighchartsOptionsImage(
    chartOptions: any,
    options: { panelWidth?: number; panelHeight?: number; padding?: number } = {}
  ) {
    const chartHost = document.createElement('div');
    chartHost.style.position = 'fixed';
    chartHost.style.left = '-10000px';
    chartHost.style.top = '0';
    chartHost.style.width = `${options.panelWidth || 760}px`;
    chartHost.style.height = `${options.panelHeight || chartOptions?.chart?.height || 460}px`;
    document.body.appendChild(chartHost);

    const chart = Highcharts.chart(chartHost, {
      ...chartOptions,
      chart: {
        ...(chartOptions.chart || {}),
        animation: false,
        width: options.panelWidth || 760,
        height: options.panelHeight || chartOptions?.chart?.height || 460
      }
    });

    try {
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      return await captureHighchartsImage({ current: { chart } }, options);
    } finally {
      chart.destroy();
      document.body.removeChild(chartHost);
    }
  }

  async function captureRenderedPanelImage(
    ref: { current: HTMLDivElement | null },
    styleOverrides: Record<string, string> = {}
  ) {
    if (!ref.current) {
      return null;
    }

    const sourceNode = ref.current;
    const sourceRect = sourceNode.getBoundingClientRect();
    const width = Math.ceil(sourceRect.width);
    const height = Math.ceil(sourceRect.height);
    const previousInlineStyles: Record<string, string> = {
      overflow: sourceNode.style.overflow,
      paddingTop: sourceNode.style.paddingTop,
      paddingRight: sourceNode.style.paddingRight,
      paddingBottom: sourceNode.style.paddingBottom,
      paddingLeft: sourceNode.style.paddingLeft,
      backgroundColor: sourceNode.style.backgroundColor
    };

    Object.assign(sourceNode.style, styleOverrides, {
      overflow: 'visible',
      backgroundColor: '#ffffff'
    });

    try {
      await new Promise((resolve) => window.requestAnimationFrame(resolve));

      const captureRect = sourceNode.getBoundingClientRect();
      const captureWidth = Math.ceil(Math.max(width, captureRect.width, sourceNode.scrollWidth));
      const captureHeight = Math.ceil(Math.max(height, captureRect.height, sourceNode.scrollHeight));

      return await toPng(sourceNode, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: '#ffffff',
        width: captureWidth,
        height: captureHeight,
        canvasWidth: captureWidth * 2,
        canvasHeight: captureHeight * 2,
        skipAutoScale: true,
        style: {
          transform: 'none',
          overflow: 'visible',
          backgroundColor: '#ffffff'
        }
      });
    } finally {
      sourceNode.style.overflow = previousInlineStyles.overflow;
      sourceNode.style.paddingTop = previousInlineStyles.paddingTop;
      sourceNode.style.paddingRight = previousInlineStyles.paddingRight;
      sourceNode.style.paddingBottom = previousInlineStyles.paddingBottom;
      sourceNode.style.paddingLeft = previousInlineStyles.paddingLeft;
      sourceNode.style.backgroundColor = previousInlineStyles.backgroundColor;
    }
  }

  function exportNarrativePdfContent() {
    if (narrativeSections.length === 0) {
      return [];
    }

    return narrativeSections.flatMap((section) => ([
      {
        text: section.title,
        fontSize: 22,
        bold: true,
        color: `#${EXPORT_SECTION_TITLE_COLOR}`,
        margin: [0, 18, 0, 10]
      },
      ...section.blocks.flatMap((block) => ([
        ...(block.title ? [{
          text: block.title,
          fontSize: 15,
          bold: true,
          color: `#${EXPORT_SECTION_TITLE_COLOR}`,
          margin: [0, 0, 0, 4]
        }] : []),
        {
          text: block.body,
          fontSize: 12,
          color: `#${EXPORT_BODY_COLOR}`,
          lineHeight: 1.45,
          margin: [0, 0, 0, 12]
        }
      ]))
    ]));
  }

  function getFamilyProgressSubtests() {
    return Array.from(new Set(computedResults.allScoreRows.map((row) => row.subtest)))
      .map((subtest) => {
        const rows = computedResults.allScoreRows
          .filter((row) => row.subtest === subtest)
          .filter((row) => isNumericScoreValue(row.scaleScore) && isNumericScoreValue(row.percentileRank))
          .sort((a, b) => {
            const dateCompare = a.completed_date_key.localeCompare(b.completed_date_key);
            if (dateCompare !== 0) {
              return dateCompare;
            }
            return a.testId - b.testId;
          });

        return {
          subtest,
          rows
        };
      })
      .filter((entry) => entry.rows.length >= 2);
  }

  function getFamilyReportSections() {
    const sectionOffset = familyHasMultipleTestDates ? 1 : 0;

    return {
      skills: `${3 + sectionOffset}.  Understanding The Skills`,
      scores: `${4 + sectionOffset}.  Understanding The Scores`,
      summary: `${5 + sectionOffset}.  Putting It All Together`,
      recommendations: `${6 + sectionOffset}.  Recommendations`,
      note: `${7 + sectionOffset}.  A Note About This Assessment`
    };
  }

  function getFamilyTableColumns() {
    return visibleTestResultExportColumns.filter((column) => column.key !== 'student_name');
  }

  function getFamilyTableRows(columns: ExportTableColumn[]) {
    return visibleScoreRows.map((row) => columns.map((column) => {
      if (column.key === 'completed_timestamp') {
        return formatDateLabel(row.completed_timestamp);
      }

      return exportValue((row as any)[column.key]);
    }));
  }

  function getPdfSectionHeader(title: string) {
    return {
      text: title,
      fontSize: 18,
      bold: true,
      color: '#1F5C99',
      margin: [0, 20, 0, 10]
    };
  }

  function getWordFamilySection(title: string) {
    return `<h2>${escapeHtml(title)}</h2>`;
  }

  function getFamilyIntroParagraphs() {
    return [
      'Dear Parent or Guardian,',
      'Thank you for your interest in your student’s reading progress. This report summarizes your student’s results from the Word Reading Assessment (WRA), a research-based assessment designed to measure key early reading skills.',
      'Please read this report carefully. We encourage you to share any questions or concerns with your child’s teacher, who can help put these results in context and discuss next steps.'
    ];
  }

  async function handleFamilyExport(format: 'pdf' | 'word') {
    if (!canGenerateFamilyReport || exportingFormat || exportingFamilyFormat) {
      return;
    }

    setExportingFamilyFormat(format);

    try {
      const [
        logoDataUrl,
        wraLogoDataUrl,
        screeningImage,
        riskHistoryImage
      ] = await Promise.all([
        fileToDataUrl(logo),
        fileToDataUrl(wraLogo),
        captureRenderedPanelImage(screeningExportRef, CHART_EXPORT_CAPTURE_STYLE),
        captureHighchartsImage(riskHistoryChartRef)
      ]);

      const familyProgressImages = familyHasMultipleTestDates
        ? await Promise.all(getFamilyProgressSubtests().map(async (entry) => ({
          subtest: entry.subtest,
          image: await captureHighchartsOptionsImage(buildProgressChartOptionsForRows(entry.rows, entry.subtest), { panelWidth: 760, panelHeight: 460 })
        })))
        : [];
      const filteredFamilyProgressImages = familyProgressImages.filter((entry) => !!entry.image);
      const familyTableColumns = getFamilyTableColumns();
      const familyTableRows = getFamilyTableRows(familyTableColumns);
      const familySections = getFamilyReportSections();
      const profile = singleStudentNarrative?.profile || null;
      const reportDateLabel = screeningMostRecentDateLabel || formatDateLabel(new Date());
      const displayStudentName = screeningStudentFullName || reportPopulationLabel || 'Student';

      if (format === 'pdf') {
        const pdfMake = require('pdfmake/build/pdfmake');
        const pdfFonts = require('pdfmake/build/vfs_fonts');

        if (!pdfMake.vfs) {
          pdfMake.vfs = pdfFonts?.pdfMake?.vfs || pdfFonts?.vfs || {};
        }

        const content: any[] = [
          {
            image: wraLogoDataUrl,
            width: 130,
            alignment: 'center',
            margin: [0, 0, 0, 10]
          },
          {
            text: 'Family Report',
            fontSize: 22,
            bold: true,
            alignment: 'center',
            color: `#${EXPORT_BODY_COLOR}`,
            margin: [0, 0, 0, 6]
          },
          {
            text: `${displayStudentName} • ${reportDateLabel}`,
            fontSize: 12,
            alignment: 'center',
            color: `#${EXPORT_MUTED_COLOR}`,
            margin: [0, 0, 0, 18]
          },
          ...getFamilyIntroParagraphs().map((paragraph) => ({
            text: paragraph,
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            lineHeight: 1.45,
            margin: [0, 0, 0, 10]
          })),
          getPdfSectionHeader(`1.  Overall Screening Result - ${reportDateLabel}`),
          {
            text: 'The WRA places students into one of three risk categories based on their current word reading skills. Your student’s result was:',
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            margin: [0, 0, 0, 8]
          }
        ];

        if (screeningImage) {
          content.push(getPdfImageBlock(screeningImage, [430, 246]));
        }

        if (riskHistoryImage && familyHasMultipleTestDates) {
          content.push(getPdfImageBlock(riskHistoryImage, [430, 188]));
        }

        content.push(
          {
            text: 'It is important to understand that this screening result is a starting point — not a final judgment. The purpose of early screening is to identify students who may benefit from extra support before reading difficulties become more significant. Many children who receive targeted intervention make excellent progress.',
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            lineHeight: 1.45,
            margin: [0, 0, 0, 12]
          },
          getPdfSectionHeader('2.  Detailed Test Results'),
          {
            text: 'In addition to the overall screening result, the WRA measures specific reading-related skills. The table below shows your child’s scores in each area.',
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            margin: [0, 0, 0, 8]
          },
          buildPdfTable(
            getPdfTestTableWidths(familyTableColumns, true),
            familyTableColumns.map((column) => column.label),
            familyTableRows
          )
        );

        if (familyHasMultipleTestDates && filteredFamilyProgressImages.length > 0) {
          filteredFamilyProgressImages.forEach((entry, index) => {
            content.push({
              unbreakable: true,
              stack: [
                ...(index === 0 ? [getPdfSectionHeader('3.  Progress Over Time')] : []),
                {
                  text: entry.subtest,
                  fontSize: 14,
                  bold: true,
                  color: `#${EXPORT_BODY_COLOR}`,
                  margin: [0, index === 0 ? 0 : 8, 0, 14]
                },
                getPdfImageBlock(entry.image!, [430, 220])
              ]
            });
          });
        }

        content.push(
          getPdfSectionHeader(familySections.skills),
          ...NARRATIVE_SKILLS_TESTED.flatMap((item) => ([
            {
              text: item.title,
              fontSize: 13,
              bold: true,
              color: `#${EXPORT_BODY_COLOR}`,
              margin: [0, 8, 0, 4]
            },
            {
              text: item.body,
              fontSize: 12,
              color: `#${EXPORT_BODY_COLOR}`,
              lineHeight: 1.45,
              margin: [0, 0, 0, 8]
            }
          ])),
          getPdfSectionHeader(familySections.scores),
          ...NARRATIVE_SCORE_DESCRIPTIONS.flatMap((item) => ([
            {
              text: item.title,
              fontSize: 13,
              bold: true,
              color: `#${EXPORT_BODY_COLOR}`,
              margin: [0, 8, 0, 4]
            },
            {
              text: item.body,
              fontSize: 12,
              color: `#${EXPORT_BODY_COLOR}`,
              lineHeight: 1.45,
              margin: [0, 0, 0, 8]
            }
          ])),
          getPdfSectionHeader(familySections.summary),
          {
            text: profile?.interpretation || 'Results should be reviewed with the student’s teacher or reading specialist to understand how they fit with classroom performance and other information.',
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            lineHeight: 1.45,
            margin: [0, 0, 0, 10]
          },
          getPdfSectionHeader(familySections.recommendations),
          {
            text: profile?.recommendation || 'Talk with your child’s teacher about whether additional reading support or monitoring is recommended.',
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            lineHeight: 1.45,
            margin: [0, 0, 0, 10]
          },
          {
            text: 'Here are some tips for helping your student continue to grow in their reading development:',
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            margin: [0, 0, 0, 8]
          },
          {
            text: 'At School',
            fontSize: 13,
            bold: true,
            color: `#${EXPORT_BODY_COLOR}`,
            margin: [0, 8, 0, 4]
          },
          {
            ul: [
              'Talk with your child’s teacher about whether additional reading support or small-group instruction in phonics and phonological awareness is needed.',
              'Talk with your child’s teacher about ways to celebrate and encourage your child’s reading success!'
            ],
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            margin: [0, 0, 0, 8]
          },
          {
            text: 'At Home',
            fontSize: 13,
            bold: true,
            color: `#${EXPORT_BODY_COLOR}`,
            margin: [0, 8, 0, 4]
          },
          {
            ul: [
              'Read aloud together every day. Even when children are learning to read independently, listening to books read aloud builds vocabulary, comprehension, and a love of reading.',
              'Play with sounds in words — rhyming games, clapping syllables, or tongue twisters are fun ways to build phonological awareness at home.',
              'Practice letter-sound relationships using phonics-based games or apps recommended by your child’s teacher.',
              'When helping your student with reading, have your student try to sound out unfamiliar words before telling your student the word.',
              'Celebrate reading effort and progress, not just accuracy. A child who feels confident and motivated is more likely to practice reading and grow.'
            ],
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            margin: [0, 0, 0, 8]
          },
          getPdfSectionHeader(familySections.note),
          {
            text: NARRATIVE_NOTE_ABOUT_ASSESSMENT,
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            lineHeight: 1.45,
            margin: [0, 0, 0, 10]
          },
          {
            text: 'We hope this report is helpful. Please don’t hesitate to reach out to your child’s teacher or school reading specialist with any questions.',
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            lineHeight: 1.45,
            margin: [0, 0, 0, 18]
          },
          {
            columns: [
              { width: '*', text: '' },
              { width: 130, image: wraLogoDataUrl, fit: [120, 58], alignment: 'center' },
              { width: 24, text: '' },
              { width: 160, image: logoDataUrl, fit: [150, 58], alignment: 'center', margin: [0, 8, 0, 0] },
              { width: '*', text: '' }
            ],
            margin: [0, 12, 0, 10]
          },
          {
            text: 'Word Reading Assessment (WRA) is developed by Ventris Learning. Results are intended to support — not replace — professional judgment.',
            fontSize: 9,
            italics: true,
            alignment: 'center',
            color: `#${EXPORT_MUTED_COLOR}`
          }
        );

        pdfMake.createPdf({
          pageSize: 'LETTER',
          pageMargins: [36, 42, 36, 42],
          content
        }).download(`${familyReportFileBaseName}.pdf`);
      } else {
        const wordTableHtml = (header: string[], rows: string[][]) => `
          <table class="results-table">
            <thead>
              <tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${rows.map((row, rowIndex) => `
                <tr class="${rowIndex % 2 === 1 ? 'alt-row' : ''}">
                  ${row.map((cell) => `<td>${escapeHtml(cell || 'N/A')}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;
        const wordImageHtml = (image: string, alt: string) => (
          `<p class="chart-image-paragraph"><img src="${image}" alt="${escapeHtml(alt)}" class="chart-image" width="600" style="width:6.25in;max-width:6.25in;height:auto;display:block;margin:10pt auto 10pt auto;" /></p><p class="chart-spacer">&nbsp;</p>`
        );
        const wordParts = [
          `<div class="logo-wrap"><img src="${wraLogoDataUrl}" alt="WRA" class="wra-brand-logo" width="150" style="width:150px;height:auto;display:block;margin:0 auto;" /></div>`,
          `<h1>Family Report</h1>`,
          `<p class="meta-line centered">${escapeHtml(`${displayStudentName} • ${reportDateLabel}`)}</p>`,
          ...getFamilyIntroParagraphs().map((paragraph) => `<p>${escapeHtml(paragraph)}</p>`),
          getWordFamilySection(`1.  Overall Screening Result - ${reportDateLabel}`),
          `<p>The WRA places students into one of three risk categories based on their current word reading skills. Your student’s result was:</p>`,
          screeningImage ? wordImageHtml(screeningImage, 'Overall Screening Result') : '',
          riskHistoryImage && familyHasMultipleTestDates ? wordImageHtml(riskHistoryImage, 'Risk Over Time') : '',
          `<p>It is important to understand that this screening result is a starting point — not a final judgment. The purpose of early screening is to identify students who may benefit from extra support before reading difficulties become more significant. Many children who receive targeted intervention make excellent progress.</p>`,
          getWordFamilySection('2.  Detailed Test Results'),
          `<p>In addition to the overall screening result, the WRA measures specific reading-related skills. The table below shows your child’s scores in each area.</p>`,
          wordTableHtml(familyTableColumns.map((column) => column.label), familyTableRows)
        ];

        if (familyHasMultipleTestDates && filteredFamilyProgressImages.length > 0) {
          wordParts.push(getWordFamilySection('3.  Progress Over Time'));
          filteredFamilyProgressImages.forEach((entry) => {
            wordParts.push(`<h3>${escapeHtml(entry.subtest)}</h3>`);
            wordParts.push(wordImageHtml(entry.image!, `Progress Over Time - ${entry.subtest}`));
          });
        }

        wordParts.push(
          getWordFamilySection(familySections.skills),
          ...NARRATIVE_SKILLS_TESTED.map((item) => `<h3>${escapeHtml(item.title)}</h3><p>${escapeHtml(item.body)}</p>`),
          getWordFamilySection(familySections.scores),
          ...NARRATIVE_SCORE_DESCRIPTIONS.map((item) => `<h3>${escapeHtml(item.title)}</h3><p>${escapeHtml(item.body)}</p>`),
          getWordFamilySection(familySections.summary),
          `<p>${escapeHtml(profile?.interpretation || 'Results should be reviewed with the student’s teacher or reading specialist to understand how they fit with classroom performance and other information.')}</p>`,
          getWordFamilySection(familySections.recommendations),
          `<p>${escapeHtml(profile?.recommendation || 'Talk with your child’s teacher about whether additional reading support or monitoring is recommended.')}</p>`,
          `<p>Here are some tips for helping your student continue to grow in their reading development:</p>`,
          `<h3>At School</h3>`,
          `<ul><li>Talk with your child’s teacher about whether additional reading support or small-group instruction in phonics and phonological awareness is needed.</li><li>Talk with your child’s teacher about ways to celebrate and encourage your child’s reading success!</li></ul>`,
          `<h3>At Home</h3>`,
          `<ul><li>Read aloud together every day. Even when children are learning to read independently, listening to books read aloud builds vocabulary, comprehension, and a love of reading.</li><li>Play with sounds in words — rhyming games, clapping syllables, or tongue twisters are fun ways to build phonological awareness at home.</li><li>Practice letter-sound relationships using phonics-based games or apps recommended by your child’s teacher.</li><li>When helping your student with reading, have your student try to sound out unfamiliar words before telling your student the word.</li><li>Celebrate reading effort and progress, not just accuracy. A child who feels confident and motivated is more likely to practice reading and grow.</li></ul>`,
          getWordFamilySection(familySections.note),
          `<p>${escapeHtml(NARRATIVE_NOTE_ABOUT_ASSESSMENT)}</p>`,
          `<p>We hope this report is helpful. Please don’t hesitate to reach out to your child’s teacher or school reading specialist with any questions.</p>`,
          `<div class="footer-logo-row"><img src="${wraLogoDataUrl}" alt="WRA" width="130" style="width:130px;height:auto;" /><img src="${logoDataUrl}" alt="Ventris Learning" width="160" style="width:160px;height:auto;" /></div>`,
          `<p class="export-footer">Word Reading Assessment (WRA) is developed by Ventris Learning. Results are intended to support — not replace — professional judgment.</p>`
        );

        const wordHtml = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office"
                xmlns:w="urn:schemas-microsoft-com:office:word"
                xmlns="http://www.w3.org/TR/REC-html40">
            <head>
              <meta charset="utf-8" />
              <meta name="ProgId" content="Word.Document" />
              <meta name="Generator" content="Microsoft Word 15" />
              <meta name="Originator" content="Microsoft Word 15" />
              <style>
                body { font-family: Calibri, Arial, sans-serif; color: #${EXPORT_BODY_COLOR}; margin: 36px; }
                h1 { font-size: 22pt; margin: 0 0 6pt 0; text-align: center; color: #${EXPORT_BODY_COLOR}; }
                h2 { font-size: 15pt; margin: 22pt 0 10pt 0; color: #1F5C99; border-bottom: 1px solid #1F5C99; padding-bottom: 4pt; }
                h3 { font-size: 12pt; margin: 12pt 0 5pt 0; color: #${EXPORT_BODY_COLOR}; }
                p, li { font-size: 11pt; line-height: 1.5; }
                p { margin: 0 0 10pt 0; }
                ul { margin-top: 0; margin-bottom: 10pt; }
                .logo-wrap { margin: 0 0 10pt 0; text-align: center; }
                .centered { text-align: center; }
                .meta-line { color: #${EXPORT_MUTED_COLOR}; }
                .chart-image-paragraph { margin: 10pt 0 0 0; text-align: center; }
                .chart-image { width: 6.25in; max-width: 6.25in; height: auto; display: block; margin: 10pt auto 14pt auto; }
                .chart-spacer { margin: 0 0 8pt 0; line-height: 8pt; font-size: 8pt; }
                .results-table { width: 100%; border-collapse: collapse; table-layout: fixed; margin: 12pt 0 16pt 0; }
                .results-table th { background: #${EXPORT_TABLE_HEADER}; color: #ffffff; font-size: 9pt; font-weight: 700; border: 1px solid #d7dbe3; padding: 5pt; text-align: left; word-break: normal; overflow-wrap: normal; }
                .results-table td { font-size: 8.5pt; border: 1px solid #d7dbe3; padding: 5pt; vertical-align: top; word-break: break-word; }
                .results-table .alt-row td { background: #${EXPORT_TABLE_ALT_ROW}; }
                .footer-logo-row { margin: 24pt 0 8pt 0; text-align: center; border-top: 1px solid #cccccc; padding-top: 12pt; }
                .footer-logo-row img { display: inline-block; vertical-align: middle; margin: 0 12pt; }
                .export-footer { color: #777777; font-size: 9pt; font-style: italic; text-align: center; }
              </style>
            </head>
            <body>
              ${wordParts.join('')}
            </body>
          </html>
        `;

        downloadBlob(
          new Blob(['\ufeff', wordHtml], { type: 'application/msword;charset=utf-8' }),
          `${familyReportFileBaseName}.doc`
        );
      }
    } catch (familyExportError) {
      console.error('Family report export failed', familyExportError);
      setError(`We couldn't generate the Family Report ${format === 'pdf' ? 'PDF' : 'Word document'} right now. Please try again.`);
    } finally {
      setExportingFamilyFormat('');
    }
  }

  async function handleExport(format: 'pdf' | 'word') {
    if (!generatedResultsAreCurrent || exportingFormat) {
      return;
    }

    setExportingFormat(format);

    try {
      const [
        logoDataUrl,
        wraLogoDataUrl,
        screeningImage,
        aggregateScreeningImage,
        riskHistoryImage,
        progressImage,
        percentileDistributionImage,
        averageScaleScoreImage
      ] = await Promise.all([
        fileToDataUrl(logo),
        fileToDataUrl(wraLogo),
        captureRenderedPanelImage(screeningExportRef, CHART_EXPORT_CAPTURE_STYLE),
        isAggregateRecentView
          ? captureElementImage(aggregateScreeningStripRef, CHART_EXPORT_CAPTURE_STYLE)
          : captureHighchartsImage(aggregateScreeningChartRef),
        captureHighchartsImage(riskHistoryChartRef),
        captureHighchartsImage(progressChartRef),
        captureHighchartsImage(percentileDistributionChartRef),
        captureHighchartsImage(averageScaleScoreChartRef)
      ]);

      if (format === 'pdf') {
        const pdfMake = require('pdfmake/build/pdfmake');
        const pdfFonts = require('pdfmake/build/vfs_fonts');

        if (!pdfMake.vfs) {
          pdfMake.vfs = pdfFonts?.pdfMake?.vfs || pdfFonts?.vfs || {};
        }

        const content: any[] = [
          {
            image: wraLogoDataUrl,
            width: 150,
            alignment: 'center',
            margin: [0, 0, 0, 18]
          },
          {
            text: 'WRA Results',
            fontSize: 24,
            bold: true,
            color: `#${EXPORT_SECTION_TITLE_COLOR}`,
            margin: [0, 0, 0, 6]
          },
          {
            text: `${reportModeLabel}: ${reportPopulationLabel}`,
            fontSize: 13,
            color: `#${EXPORT_MUTED_COLOR}`,
            margin: [0, 0, 0, 2]
          },
          {
            text: screeningExportCaption,
            fontSize: 13,
            color: `#${EXPORT_MUTED_COLOR}`,
            margin: [0, 0, 0, 18]
          }
        ];

        content.push(
          {
            text: 'Screening',
            fontSize: 20,
            bold: true,
            color: `#${EXPORT_SECTION_TITLE_COLOR}`,
            margin: [0, 10, 0, 10]
          }
        );

        if (reportPopulationIsSingleStudent) {
          content.push({
            text: 'The WRA places students into one of three risk categories based on their current word reading skills. Your student’s result was:',
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            margin: [0, 0, 0, 8]
          });

          if (isSingleStudentMultipleTests && screeningMostRecentDateLabel) {
            content.push({
              text: `Most Recent Screening: ${screeningMostRecentDateLabel}`,
              fontSize: 12,
              color: `#${EXPORT_BODY_COLOR}`,
              margin: [0, 0, 0, 8]
            });
          }

          if (screeningImage) {
            content.push(getPdfImageBlock(screeningImage, [430, 246]));
          }

          content.push({
            text: 'It is important to understand that this screening result is a starting point, not a final judgment. The purpose of early screening is to identify students who may benefit from extra support before reading difficulties become more significant.',
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            margin: [0, 0, 0, 14]
          });
        } else if (isAggregateRecentView) {
          content.push({
            text: 'This view summarizes the latest screening results for WRA Testing.',
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`,
            margin: [0, 0, 0, 8]
          });

          if (aggregateScreeningImage) {
            content.push(getPdfImageBlock(aggregateScreeningImage, [430, 156]));
          }

          content.push(buildPdfTable(
            ['*', 110, 150],
            aggregateRiskExportColumns.map((column) => column.label),
            aggregateRiskExportRows
          ));
        } else if (aggregateScreeningImage) {
          content.push({
            text: `Display By: ${aggregateDisplayBy === 'month' ? 'Month' : 'Norming Period'}`,
            fontSize: 12,
            color: `#${EXPORT_MUTED_COLOR}`,
            margin: [0, 0, 0, 8]
          });
          content.push(getPdfImageBlock(aggregateScreeningImage, [430, 216]));
        }

        if (riskHistoryImage) {
          content.push(
            {
              text: 'Risk Over Time',
              fontSize: 20,
              bold: true,
              color: `#${EXPORT_SECTION_TITLE_COLOR}`,
              margin: [0, 18, 0, 8]
            },
            getPdfImageBlock(riskHistoryImage, [430, 188])
          );
        }

        content.push(
          {
            text: 'Test Results',
            fontSize: 20,
            bold: true,
            color: `#${EXPORT_SECTION_TITLE_COLOR}`,
            margin: [0, 16, 0, 8]
          },
          {
            text: 'In addition to the overall screening result, the WRA measures specific reading-related skills.',
            fontSize: 12,
            color: `#${EXPORT_BODY_COLOR}`
          },
          buildPdfTable(
            getPdfTestTableWidths(visibleTestResultExportColumns, reportPopulationIsSingleStudent),
            visibleTestResultExportColumns.map((column) => column.label),
            visibleTestResultExportRows
          )
        );

        if (progressImage) {
          content.push(
            {
              text: 'Progress Over Time',
              fontSize: 20,
              bold: true,
              color: `#${EXPORT_SECTION_TITLE_COLOR}`,
              margin: [0, 18, 0, 8]
            },
            getPdfImageBlock(progressImage, [430, 198])
          );
        }

        if (percentileDistributionImage) {
          content.push(
            {
              text: 'Percentile Rank Distribution',
              fontSize: 20,
              bold: true,
              color: `#${EXPORT_SECTION_TITLE_COLOR}`,
              margin: [0, 18, 0, 8]
            },
            {
              text: `Subtest: ${distributionSubtest}`,
              fontSize: 12,
              color: `#${EXPORT_MUTED_COLOR}`,
              margin: [0, 0, 0, 8]
            },
            getPdfImageBlock(percentileDistributionImage, [430, 198])
          );
        }

        if (averageScaleScoreImage) {
          content.push(
            {
              text: 'Average Scale Score',
              fontSize: 20,
              bold: true,
              color: `#${EXPORT_SECTION_TITLE_COLOR}`,
              margin: [0, 18, 0, 8]
            },
            {
              text: `Display By: ${aggregateDisplayBy === 'month' ? 'Month' : 'Norming Period'} | Subtest: ${averageSubtest}`,
              fontSize: 12,
              color: `#${EXPORT_MUTED_COLOR}`,
              margin: [0, 0, 0, 8]
            },
            getPdfImageBlock(averageScaleScoreImage, [430, 198])
          );
        }

        content.push(...exportNarrativePdfContent());
        content.push({
          columns: [
            { width: '*', text: '' },
            { width: 130, image: wraLogoDataUrl, fit: [120, 58], alignment: 'center' },
            { width: 24, text: '' },
            { width: 160, image: logoDataUrl, fit: [150, 58], alignment: 'center', margin: [0, 8, 0, 0] },
            { width: '*', text: '' }
          ],
          margin: [0, 24, 0, 10]
        });
        content.push({
          text: 'Word Reading Assessment (WRA) is developed by Ventris Learning. Results are intended to support, not replace, professional judgment.',
          fontSize: 10,
          italics: true,
          color: `#${EXPORT_BODY_COLOR}`,
          alignment: 'center',
          margin: [0, 0, 0, 0]
        });

        pdfMake.createPdf({
          pageSize: 'LETTER',
          pageMargins: [28, 36, 28, 36],
          content
        }).download(`${exportFileBaseName}.pdf`);
      } else {
        const wordTableHtml = (header: string[], rows: string[][]) => `
          <table class="results-table">
            <thead>
              <tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join('')}</tr>
            </thead>
            <tbody>
              ${rows.map((row, rowIndex) => `
                <tr class="${rowIndex % 2 === 1 ? 'alt-row' : ''}">
                  ${row.map((cell) => `<td>${escapeHtml(cell || 'N/A')}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;

        const wordNarrativeHtml = narrativeSections.map((section) => `
          <section class="narrative-section">
            <h2>${escapeHtml(section.title)}</h2>
            ${section.blocks.map((block) => `
              <div class="narrative-block">
                ${block.title ? `<h3>${escapeHtml(block.title)}</h3>` : ''}
                <p>${escapeHtml(block.body)}</p>
              </div>
            `).join('')}
          </section>
        `).join('');

        const wordImageHtml = (image: string, alt: string) => (
          `<img src="${image}" alt="${escapeHtml(alt)}" class="chart-image" width="600" style="width:6.25in;max-width:6.25in;height:auto;display:block;margin:10pt 0 14pt 0;" />`
        );

        const wordParts: string[] = [
          `<div class="logo-wrap"><img src="${wraLogoDataUrl}" alt="WRA" class="wra-brand-logo" width="170" style="width:170px;height:auto;display:block;margin:0 auto;" /></div>`,
          `<h1>WRA Results</h1>`,
          `<p class="meta-line">${escapeHtml(`${reportModeLabel}: ${reportPopulationLabel}`)}</p>`,
          `<p class="meta-line">${escapeHtml(screeningExportCaption)}</p>`,
          `<section><h2>Screening</h2>`
        ];

        if (reportPopulationIsSingleStudent) {
          wordParts.push(`<p>The WRA places students into one of three risk categories based on their current word reading skills. Your student’s result was:</p>`);

          if (isSingleStudentMultipleTests && screeningMostRecentDateLabel) {
            wordParts.push(`<p>Most Recent Screening: ${escapeHtml(screeningMostRecentDateLabel)}</p>`);
          }

          if (screeningImage) {
            wordParts.push(wordImageHtml(screeningImage, 'Screening'));
          }

          wordParts.push(`<p>It is important to understand that this screening result is a starting point, not a final judgment. The purpose of early screening is to identify students who may benefit from extra support before reading difficulties become more significant.</p>`);
        } else if (isAggregateRecentView) {
          wordParts.push(`<p>This view summarizes the latest screening results for WRA Testing.</p>`);

          if (aggregateScreeningImage) {
            wordParts.push(wordImageHtml(aggregateScreeningImage, 'Aggregate screening'));
          }

          wordParts.push(wordTableHtml(
            aggregateRiskExportColumns.map((column) => column.label),
            aggregateRiskExportRows
          ));
        } else if (aggregateScreeningImage) {
          wordParts.push(`<p class="meta-line">Display By: ${escapeHtml(aggregateDisplayBy === 'month' ? 'Month' : 'Norming Period')}</p>`);
          wordParts.push(wordImageHtml(aggregateScreeningImage, 'Aggregate screening'));
        }

        wordParts.push(`</section>`);

        if (riskHistoryImage) {
          wordParts.push(`
            <section>
              <h2>Risk Over Time</h2>
              ${wordImageHtml(riskHistoryImage, 'Risk Over Time')}
            </section>
          `);
        }

        wordParts.push(`
          <section>
            <h2>Test Results</h2>
            <p>In addition to the overall screening result, the WRA measures specific reading-related skills.</p>
            ${wordTableHtml(
              visibleTestResultExportColumns.map((column) => column.label),
              visibleTestResultExportRows
            )}
          </section>
        `);

        if (progressImage) {
          wordParts.push(`
            <section>
              <h2>Progress Over Time</h2>
              ${wordImageHtml(progressImage, 'Progress Over Time')}
            </section>
          `);
        }

        if (percentileDistributionImage) {
          wordParts.push(`
            <section>
              <h2>Percentile Rank Distribution</h2>
              <p class="meta-line">Subtest: ${escapeHtml(distributionSubtest)}</p>
              ${wordImageHtml(percentileDistributionImage, 'Percentile Rank Distribution')}
            </section>
          `);
        }

        if (averageScaleScoreImage) {
          wordParts.push(`
            <section>
              <h2>Average Scale Score</h2>
              <p class="meta-line">Display By: ${escapeHtml(aggregateDisplayBy === 'month' ? 'Month' : 'Norming Period')} | Subtest: ${escapeHtml(averageSubtest)}</p>
              ${wordImageHtml(averageScaleScoreImage, 'Average Scale Score')}
            </section>
          `);
        }

        wordParts.push(wordNarrativeHtml);
        wordParts.push(`
          <div class="footer-logo-row">
            <img src="${wraLogoDataUrl}" alt="WRA" class="footer-wra-logo" width="130" style="width:130px;height:auto;" />
            <img src="${logoDataUrl}" alt="Ventris Learning" class="footer-ventris-logo" width="160" style="width:160px;height:auto;" />
          </div>
        `);
        wordParts.push(`<p class="export-footer">Word Reading Assessment (WRA) is developed by Ventris Learning. Results are intended to support, not replace, professional judgment.</p>`);

        const wordHtml = `
          <html xmlns:o="urn:schemas-microsoft-com:office:office"
                xmlns:w="urn:schemas-microsoft-com:office:word"
                xmlns="http://www.w3.org/TR/REC-html40">
            <head>
              <meta charset="utf-8" />
              <meta name="ProgId" content="Word.Document" />
              <meta name="Generator" content="Microsoft Word 15" />
              <meta name="Originator" content="Microsoft Word 15" />
              <style>
                body { font-family: Calibri, Arial, sans-serif; color: #${EXPORT_BODY_COLOR}; margin: 32px; }
                h1 { font-size: 30pt; margin: 0 0 10pt 0; color: #${EXPORT_SECTION_TITLE_COLOR}; }
                h2 { font-size: 20pt; margin: 22pt 0 10pt 0; color: #${EXPORT_SECTION_TITLE_COLOR}; }
                h3 { font-size: 15pt; margin: 12pt 0 6pt 0; color: #${EXPORT_SECTION_TITLE_COLOR}; }
                p { font-size: 11pt; line-height: 1.5; margin: 0 0 10pt 0; }
                .logo-wrap { margin: 0 0 18pt 0; text-align: center; }
                .wra-brand-logo { width: 170px; height: auto; }
                .meta-line { color: #${EXPORT_MUTED_COLOR}; }
                .chart-image { width: 6.25in; max-width: 6.25in; height: auto; display: block; margin: 10pt 0 14pt 0; }
                .results-table { width: 100%; border-collapse: collapse; table-layout: fixed; margin: 12pt 0 16pt 0; }
                .results-table th { background: #${EXPORT_TABLE_HEADER}; color: #ffffff; font-size: 10pt; font-weight: 700; border: 1px solid #d7dbe3; padding: 6pt; text-align: left; word-break: normal; overflow-wrap: normal; }
                .results-table td { font-size: 9pt; border: 1px solid #d7dbe3; padding: 6pt; vertical-align: top; word-break: break-word; }
                .results-table .alt-row td { background: #${EXPORT_TABLE_ALT_ROW}; }
                .narrative-section { margin-top: 22pt; }
                .narrative-block + .narrative-block { margin-top: 12pt; }
                .footer-logo-row { margin: 24pt 0 8pt 0; text-align: center; }
                .footer-logo-row img { display: inline-block; vertical-align: middle; margin: 0 12pt; }
                .footer-wra-logo { width: 130px; height: auto; }
                .footer-ventris-logo { width: 160px; height: auto; }
                .export-footer { margin-top: 0; font-size: 9pt; font-style: italic; text-align: center; }
              </style>
            </head>
            <body>
              ${wordParts.join('')}
            </body>
          </html>
        `;

        downloadBlob(
          new Blob(['\ufeff', wordHtml], { type: 'application/msword;charset=utf-8' }),
          `${exportFileBaseName}.doc`
        );
      }
    } catch (exportError) {
      console.error('Results export failed', exportError);
      setError(`We couldn't generate the ${format === 'pdf' ? 'PDF' : 'Word document'} right now. Please try again.`);
    } finally {
      setExportingFormat('');
    }
  }

  return (
    <Container fluid className="ventris-content-wrapper ventris-results-page">
      <Header portal={sessionStorage.getItem("portal")!} page="results" />
      <Container as="main" id="main-content" tabIndex={-1}>
        <Row>
          <Col>
            <div className="results-page-shell">
              <AsyncStatus busy={loadingReferenceData} busyMessage="Loading report options." completeMessage="Report options loaded." />
              <AsyncStatus busy={loadingResults} busyMessage="Generating results." completeMessage="Results are ready." />
              <AsyncStatus
                busy={exportingFormat !== "" || exportingFamilyFormat !== ""}
                busyMessage="Generating report download."
                completeMessage="Report download is ready."
              />
              <h1 className="page-header page-header-no-margin align-items-center">
                <ReportPageIcon />
                <div className="flex-grow">Results</div>
              </h1>

              <div className="results-parameter-panel" aria-busy={loadingReferenceData || loadingResults}>
                <h3>Select Parameters</h3>
                <p className="results-intro-copy">
                  First, specify whether you&apos;d like to view results by {terms.student.toLowerCase()} (single or multiple), by {terms.class.toLowerCase()}, or by group.
                </p>

                <Row>
                  <Col lg={4} md={12}>
                    <Form.Group className="ventris-form-element">
                      <Form.Label>By {terms.student}</Form.Label>
                      <SearchableMultiSelect
                        options={studentOptions}
                        values={selectedStudentIds}
                        onChange={(values) => {
                          setSelectedStudentIds(values);
                          setSelectedTestId("");
                          if (values.length > 0) {
                            setSelectedClassId("");
                            setSelectedGroupId("");
                          }
                        }}
                        ariaLabel={`Select ${terms.students}`}
                        placeholder={`Select ${terms.student}(s)`}
                        singularLabel={terms.student}
                        pluralLabel={terms.students}
                        disabled={loadingReferenceData}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={4} md={12}>
                    <Form.Group className="ventris-form-element">
                      <Form.Label>By {terms.class}</Form.Label>
                      <SearchableSelect
                        options={classOptions}
                        value={selectedClassId}
                        onChange={(value) => {
                          setSelectedClassId(value);
                          setSelectedTestId("");
                          if (value) {
                            setSelectedStudentIds([]);
                            setSelectedGroupId("");
                          }
                        }}
                        ariaLabel={`Select ${terms.class}`}
                        includeEmptyOption={true}
                        emptyOptionLabel={`Select ${terms.class}`}
                        placeholder={`Select ${terms.class}`}
                        disabled={loadingReferenceData}
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={4} md={12}>
                    <Form.Group className="ventris-form-element">
                      <Form.Label>By Group</Form.Label>
                      <SearchableSelect
                        options={groupOptions}
                        value={selectedGroupId}
                        onChange={(value) => {
                          setSelectedGroupId(value);
                          setSelectedTestId("");
                          if (value) {
                            setSelectedStudentIds([]);
                            setSelectedClassId("");
                          }
                        }}
                        ariaLabel="Select Group"
                        includeEmptyOption={true}
                        emptyOptionLabel="Select Group"
                        placeholder="Select Group"
                        disabled={loadingReferenceData}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <p className="results-intro-copy results-timeframe-copy">
                  Next, select the time frame you wish to view results by:
                </p>

                <Row className="results-timeframe-row">
                  <Col lg={4} md={12}>
                    <button
                      type="button"
                      className={`results-timeframe-option ${timeframe === 'recent' ? 'results-timeframe-option-selected' : ''}`}
                      onClick={() => setTimeframe('recent')}
                    >
                      <span className={`results-timeframe-checkbox ${timeframe === 'recent' ? 'results-timeframe-checkbox-selected' : ''}`}>
                        {timeframe === 'recent' ? '✓' : ''}
                      </span>
                      <span>Most Recent Screening</span>
                    </button>
                  </Col>
                  <Col lg={4} md={12}>
                    <button
                      type="button"
                      className={`results-timeframe-option ${timeframe === 'ytd' ? 'results-timeframe-option-selected' : ''}`}
                      onClick={() => setTimeframe('ytd')}
                    >
                      <span className={`results-timeframe-checkbox ${timeframe === 'ytd' ? 'results-timeframe-checkbox-selected' : ''}`}>
                        {timeframe === 'ytd' ? '✓' : ''}
                      </span>
                      <span>All Screenings: Current School Year to Date</span>
                    </button>
                  </Col>
                  <Col lg={4} md={12}>
                    <button
                      type="button"
                      className={`results-timeframe-option ${timeframe === 'range' ? 'results-timeframe-option-selected' : ''}`}
                      onClick={() => setTimeframe('range')}
                    >
                      <span className={`results-timeframe-checkbox ${timeframe === 'range' ? 'results-timeframe-checkbox-selected' : ''}`}>
                        {timeframe === 'range' ? '✓' : ''}
                      </span>
                      <span>Specific Date or Range</span>
                    </button>
                  </Col>
                </Row>

                {timeframe === 'range' ? (
                  <Row className="results-date-range-row">
                    <Col lg={3} md={6}>
                      <Form.Group className="ventris-form-element" controlId="results-start-date">
                        <Form.Label>Start Date</Form.Label>
                        <Form.Control type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
                      </Form.Group>
                    </Col>
                    <Col lg={3} md={6}>
                      <Form.Group className="ventris-form-element" controlId="results-end-date">
                        <Form.Label>End Date</Form.Label>
                        <Form.Control type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
                      </Form.Group>
                    </Col>
                  </Row>
                ) : null}

                {error ? <div role="alert" className="results-error">{error}</div> : null}

                <div className="results-button-row">
                  <Button
                    variant="primary"
                    className="ventris-button-primary ventris-button-primary-blue"
                    onClick={() => {
                      setSelectedTestId("");
                      generateResults();
                    }}
                    disabled={loadingReferenceData || loadingResults || exportingFormat !== "" || exportingFamilyFormat !== ""}
                  >
                    {loadingResults ? 'Generating...' : 'Generate Results'}
                  </Button>
                  <Dropdown className="results-export-dropdown">
                    <Dropdown.Toggle
                      variant="secondary"
                      className="ventris-button-secondary ventris-button-secondary-blue"
                      disabled={!generatedResultsAreCurrent || loadingReferenceData || loadingResults || exportingFormat !== "" || exportingFamilyFormat !== ""}
                    >
                      <DownloadIcon /> {exportingFormat ? `Generating ${exportingFormat === 'pdf' ? 'PDF' : 'Word'}...` : 'Download as'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleExport('pdf')}>PDF</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleExport('word')}>Word Document</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                  <Dropdown className="results-export-dropdown">
                    <Dropdown.Toggle
                      variant="secondary"
                      className="ventris-button-secondary ventris-button-secondary-blue"
                      disabled={!canGenerateFamilyReport || loadingReferenceData || loadingResults || exportingFormat !== "" || exportingFamilyFormat !== ""}
                    >
                      <DownloadIcon /> {exportingFamilyFormat ? `Generating Family ${exportingFamilyFormat === 'pdf' ? 'PDF' : 'Word'}...` : 'Generate Family Report'}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => handleFamilyExport('pdf')}>PDF</Dropdown.Item>
                      <Dropdown.Item onClick={() => handleFamilyExport('word')}>Word Document</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>

              {!hasGeneratedResults ? (
                <div className="results-empty-state">
                  Your results will appear here once you&apos;ve specified parameters above.
                </div>
              ) : (
                <div className="results-report-shell" aria-busy={loadingResults}>
                  <div className="results-divider" />

                  <section className="results-section">
                    <h3>Screening</h3>
                    {reportPopulationIsSingleStudent ? (
                      <>
                        <p className="results-section-copy">
                          The WRA places students into one of three risk categories based on their current word reading skills.
                          Your student&apos;s result was:
                        </p>
                        {isSingleStudentMultipleTests && screeningMostRecentDateLabel ? (
                          <p className="results-section-copy">
                            <strong>Most Recent Screening:</strong> {screeningMostRecentDateLabel}
                          </p>
                        ) : null}
                        {screeningRiskContent && ((screeningNarrativeOnly && screeningDisplayRiskCategory) || (screeningConfig && screeningScaleScore !== null)) ? (
                          <>
                            <div ref={screeningExportRef}>
                              <div className="results-screening-summary-card">
                                <div className="results-screening-summary-column results-screening-summary-column-left">
                                  <div className="results-screening-heading-row">
                                    <span className={`results-screening-icon results-screening-icon-${(screeningDisplayRiskCategory || '').toLowerCase()}`}>
                                      {screeningDisplayRiskCategory === 'High' ? '↑' : screeningDisplayRiskCategory === 'Moderate' ? '−' : '↓'}
                                    </span>
                                    <div className="results-screening-heading">{screeningRiskContent.title}</div>
                                  </div>
                                  <div className="results-screening-copy">
                                    <strong>{screeningStudentFullName}</strong>&apos;s word reading skills are {screeningDisplayRiskCategory === 'Low' ? 'at or above the level expected for their grade at this time of year.' : screeningDisplayRiskCategory === 'Moderate' ? 'approaching, but still below, the level expected for their grade at this time of year.' : 'well below the level expected for their grade at this time of year.'}
                                  </div>
                                </div>
                                <div className="results-screening-summary-column">
                                  <div className="results-screening-heading-row">
                                    <span className="results-screening-icon results-screening-icon-meaning">?</span>
                                    <div className="results-screening-heading">What This Means</div>
                                  </div>
                                  <div className="results-screening-copy">
                                    {screeningRiskContent.meaning}
                                  </div>
                                </div>
                              </div>

                              {!screeningNarrativeOnly ? (
                                <div className="results-screening-band-card">
                                  <div className="results-screening-band-labels">
                                    <div className="results-screening-band-boundary results-screening-band-boundary-left">
                                      <div className="results-screening-band-caption">Minimum<br />Score</div>
                                      <div className="results-screening-band-value">{screeningAxisMin}</div>
                                      <div className="results-screening-boundary-line results-screening-boundary-line-left" />
                                    </div>
                                    <div className={`results-screening-band-boundary results-screening-band-boundary-center ${screeningMarkerEdgeClass}`} style={{ left: `${screeningMarkerLeft}%` }}>
                                      <div className="results-screening-band-score-label">
                                        <div className="results-screening-band-caption">{screeningStudentFullName.toUpperCase()}&apos;S<br />SCORE</div>
                                        <div className="results-screening-band-value">{screeningScaleScore}</div>
                                      </div>
                                      <div className="results-screening-boundary-line results-screening-boundary-line-center" />
                                    </div>
                                    <div className="results-screening-band-boundary results-screening-band-boundary-right">
                                      <div className="results-screening-band-caption">Maximum<br />Score</div>
                                      <div className="results-screening-band-value">{screeningAxisMax}</div>
                                      <div className="results-screening-boundary-line results-screening-boundary-line-right" />
                                    </div>
                                  </div>

                                  <div className="results-screening-band-track">
                                    <div className="results-screening-band-segment results-screening-band-segment-high" style={{ width: `${screeningHighWidth}%` }}>
                                      <span className="results-screening-band-symbol">↑</span>
                                    </div>
                                    <div className="results-screening-band-segment results-screening-band-segment-moderate" style={{ width: `${screeningModerateWidth}%` }}>
                                      <span className="results-screening-band-symbol">−</span>
                                    </div>
                                    <div className="results-screening-band-segment results-screening-band-segment-low" style={{ width: `${screeningLowWidth}%` }}>
                                      <span className="results-screening-band-symbol">↓</span>
                                    </div>
                                    <div className="results-screening-band-marker" style={{ left: `${screeningMarkerLeft}%` }}>
                                      <div className="results-screening-band-marker-line" />
                                      <div className="results-screening-band-marker-arrow">▼</div>
                                    </div>
                                  </div>

                                  <div className="results-screening-band-legend">
                                    <div className="results-screening-band-legend-item">
                                      <span className="results-screening-icon results-screening-icon-high">↑</span>
                                      <span>High Risk</span>
                                    </div>
                                    <div className="results-screening-band-legend-item">
                                      <span className="results-screening-icon results-screening-icon-moderate">−</span>
                                      <span>Moderate Risk</span>
                                    </div>
                                    <div className="results-screening-band-legend-item">
                                      <span className="results-screening-icon results-screening-icon-low">↓</span>
                                      <span>Low Risk</span>
                                    </div>
                                  </div>
                                </div>
                              ) : null}
                            </div>

                            <p className="results-section-copy results-screening-footnote">
                              It is important to understand that this screening result is a starting point, not a final judgment. The purpose of early screening is to identify students who may benefit from extra support before reading difficulties become more significant.
                            </p>
                          </>
                        ) : (
                          <div className="results-empty-state results-inline-empty-state">
                            No screening result is available for the selected filters.
                          </div>
                        )}
                        {singleStudentRiskHistoryOptions && isSingleStudentMultipleTests ? (
                          <>
                            <p className="results-section-copy">
                              Over multiple test dates, you can see how risk status changes over time.
                            </p>
                            <div className="results-chart-panel" ref={riskHistoryChartPanelRef}>
                              <AccessibleChart
                                ref={riskHistoryChartRef}
                                options={singleStudentRiskHistoryOptions}
                                title="Risk over time"
                                description="The student's risk classification on each completed test date. High, moderate, and low risk points are also distinguished with up, dash, and down symbols."
                              />
                            </div>
                          </>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <p className="results-section-copy">
                          {isAggregateMultiTestView
                            ? `This view summarizes screening results across the selected time period for ${reportRequest?.populationLabel}.`
                            : `This view summarizes the latest screening results for ${reportRequest?.populationLabel}.`}
                        </p>
                        {isAggregateMultiTestView && aggregateScreeningChartOptions ? (
                          <>
                            <Row>
                              <Col lg={4} md={12}>
                                <Form.Group className="ventris-form-element" controlId="aggregate-screening-display-by">
                                  <Form.Label>Display By</Form.Label>
                                  <Form.Select value={aggregateDisplayBy} onChange={(event) => setAggregateDisplayBy(event.target.value as "month" | "norming")}>
                                    <option value="norming">Norming Period</option>
                                    <option value="month">Month</option>
                                  </Form.Select>
                                </Form.Group>
                              </Col>
                            </Row>
                            <div className="results-chart-panel" ref={aggregateScreeningChartPanelRef}>
                              <AccessibleChart
                                ref={aggregateScreeningChartRef}
                                options={aggregateScreeningChartOptions}
                                title="Students by risk category"
                                description="Counts of students at high, moderate, and low risk for each selected reporting period."
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="results-risk-summary-strip" ref={aggregateScreeningStripRef}>
                              {[
                                {
                                  key: 'high',
                                  label: 'High Risk',
                                  icon: '↑',
                                  count: aggregateRiskSummary.High,
                                  colorClass: 'results-screening-icon-high'
                                },
                                {
                                  key: 'moderate',
                                  label: 'Moderate Risk',
                                  icon: '−',
                                  count: aggregateRiskSummary.Moderate,
                                  colorClass: 'results-screening-icon-moderate'
                                },
                                {
                                  key: 'low',
                                  label: 'Low Risk',
                                  icon: '↓',
                                  count: aggregateRiskSummary.Low,
                                  colorClass: 'results-screening-icon-low'
                                }
                              ].map((item, index) => (
                                <div
                                  key={item.key}
                                  className={`results-risk-summary-strip-item ${index < 2 ? 'results-risk-summary-strip-item-bordered' : ''}`}
                                >
                                  <span className={`results-screening-icon ${item.colorClass}`}>{item.icon}</span>
                                  <div className="results-risk-summary-strip-content">
                                    <div className="results-risk-summary-strip-label">{item.label}</div>
                                    <div className="results-risk-summary-strip-value">
                                      {item.count} {item.count === 1 ? terms.student : terms.students}
                                    </div>
                                    <div className="results-risk-summary-strip-meta">
                                      {aggregateRiskTotal > 0 ? Math.round((item.count / aggregateRiskTotal) * 100) : 0}% of {reportRequest?.mode === 'class' ? 'class' : reportRequest?.mode === 'group' ? 'group' : 'selection'}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <ResponsiveTableRegion label="Screening risk results" className="ventris-students results-risk-table">
                              <BootstrapTable
                                keyField='id'
                                data={aggregateRiskRows}
                                columns={aggregateRiskColumns}
                                bordered={true}
                                pagination={paginationFactory({ sizePerPage: 12, hideSizePerPage: false })}
                                wrapperClasses="react-bootstrap-table-rounded"
                              />
                            </ResponsiveTableRegion>
                          </>
                        )}
                      </>
                    )}
                  </section>

                  <section className="results-section">
                    <div className="flex flex-row">
                      <h3 className="flex-grow">Test Results</h3>
                      <div className="ventris-action-links">
                        <Button variant="link" size="sm" className="ventris-action-link" onClick={() => setShowTableFilters(!showTableFilters)}>
                          <FilterIcon /> {showTableFilters ? 'Hide Filter' : 'Show Filter'}
                        </Button>
                      </div>
                    </div>
                    <p className="results-section-copy">
                      In addition to the overall screening result, the WRA measures specific reading-related skills.
                    </p>
                    {showTableFilters ? (
                      <div className="results-filter-grid">
                        <Row>
                          <Col lg={4} md={6}>
                            <Form.Group className="ventris-form-element" controlId="results-subtest-filter">
                              <Form.Label>Subtest</Form.Label>
                              <Form.Select value={subtestFilter} onChange={(event) => setSubtestFilter(event.target.value)}>
                                <option value="All">All</option>
                                {availableSubtests.map((subtest) => (
                                  <option key={subtest} value={subtest}>{subtest}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col lg={4} md={6}>
                            <Form.Group className="ventris-form-element" controlId="results-norms-filter">
                              <Form.Label>Norms</Form.Label>
                              <Form.Select value={normsFilter} onChange={(event) => setNormsFilter(event.target.value)}>
                                <option value="All">All</option>
                                {availableNorms.map((norm) => (
                                  <option key={norm} value={norm}>{norm}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col lg={4} md={12}>
                            <Form.Group className="ventris-form-element" controlId="results-test-date-filter">
                              <Form.Label>Test Date</Form.Label>
                              <Form.Select value={testDateFilter} onChange={(event) => setTestDateFilter(event.target.value)}>
                                <option value="All">All</option>
                                {availableTestDates.map(([key, label]) => (
                                  <option key={key} value={key}>{label}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    ) : null}
                    <ResponsiveTableRegion label="Test results" className={reportPopulationIsSingleStudent ? "ventris-groups results-test-results-table" : "ventris-groups results-aggregate-test-results-table"}>
                      <BootstrapTable
                        keyField='id'
                        data={visibleScoreRows}
                        columns={testResultsColumns}
                        bordered={true}
                        pagination={paginationFactory({ sizePerPage: 12, hideSizePerPage: false })}
                        wrapperClasses="react-bootstrap-table-rounded"
                      />
                    </ResponsiveTableRegion>
                  </section>

                  {reportPopulationIsSingleStudent ? (
                    progressChartOptions ? (
                      <section className="results-section">
                        <h3>Progress Over Time</h3>
                        <p className="results-section-copy">
                          This chart shows how the selected student&apos;s performance has changed over time for a specific subtest.
                        </p>
                        <Row>
                          <Col lg={4} md={12}>
                            <Form.Group className="ventris-form-element" controlId="results-progress-subtest">
                              <Form.Label>Subtest</Form.Label>
                              <Form.Select value={progressSubtest} onChange={(event) => setProgressSubtest(event.target.value)}>
                                {progressSubtestOptions.map((subtest) => (
                                  <option key={subtest} value={subtest}>{subtest}</option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="results-chart-panel" ref={progressChartPanelRef}>
                          <AccessibleChart
                            ref={progressChartRef}
                            options={progressChartOptions}
                            title={`Progress over time${progressSubtest ? ` for ${progressSubtest}` : ''}`}
                            description="Scale score and national percentile rank for each completed test date. The two measures use different chart shapes as well as different colors."
                          />
                        </div>
                      </section>
                    ) : null
                  ) : (
                    <>
                      {percentileDistributionChartOptions ? (
                        <section className="results-section">
                          <h3>Percentile Rank Distribution</h3>
                          <Row>
                            <Col lg={4} md={12}>
                              <Form.Group className="ventris-form-element" controlId="results-distribution-subtest">
                                <Form.Label>Subtest</Form.Label>
                                <Form.Select value={distributionSubtest} onChange={(event) => setDistributionSubtest(event.target.value)}>
                                  {availableSubtests.map((subtest) => (
                                    <option key={subtest} value={subtest}>{subtest}</option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                          </Row>
                          <div className="results-chart-panel" ref={percentileDistributionChartPanelRef}>
                            <AccessibleChart
                              ref={percentileDistributionChartRef}
                              options={percentileDistributionChartOptions}
                              title={`National percentile rank distribution${distributionSubtest ? ` for ${distributionSubtest}` : ''}`}
                              description="Each student's national percentile rank for the selected subtest."
                            />
                          </div>
                        </section>
                      ) : null}

                      {averageScaleScoreChartOptions ? (
                        <section className="results-section">
                          <h3>Average Scale Score</h3>
                          <Row>
                            <Col lg={4} md={12}>
                              <Form.Group className="ventris-form-element" controlId="average-score-display-by">
                                <Form.Label>Display By</Form.Label>
                                <Form.Select value={aggregateDisplayBy} onChange={(event) => setAggregateDisplayBy(event.target.value as "month" | "norming")}>
                                  <option value="norming">Norming Period</option>
                                  <option value="month">Month</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col lg={4} md={12}>
                              <Form.Group className="ventris-form-element" controlId="average-score-subtest">
                                <Form.Label>Subtest</Form.Label>
                                <Form.Select value={averageSubtest} onChange={(event) => setAverageSubtest(event.target.value)}>
                                  {availableSubtests.map((subtest) => (
                                    <option key={subtest} value={subtest}>{subtest}</option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                          </Row>
                          <div className="results-chart-panel" ref={averageScaleScoreChartPanelRef}>
                            <AccessibleChart
                              ref={averageScaleScoreChartRef}
                              options={averageScaleScoreChartOptions}
                              title={`Average scale score${averageSubtest ? ` for ${averageSubtest}` : ''}`}
                              description="The average scale score for each selected reporting period."
                            />
                          </div>
                        </section>
                      ) : null}
                    </>
                  )}

                  {narrativeSections.length > 0 ? (
                    <section className="results-section results-narrative-section">
                      <div className="results-narrative-list">
                        {narrativeSections.map((section) => {
                          const isOpen = openNarrativeSections.includes(section.id);

                          return (
                            <div key={section.id} className="results-narrative-item">
                              <button
                                type="button"
                                className="results-narrative-trigger"
                                onClick={() => {
                                  setOpenNarrativeSections((currentSections) =>
                                    currentSections.includes(section.id)
                                      ? currentSections.filter((currentSection) => currentSection !== section.id)
                                      : [...currentSections, section.id]
                                  );
                                }}
                              >
                                <span className="results-narrative-title">{section.title}</span>
                                <span className="results-narrative-toggle">{isOpen ? '−' : '+'}</span>
                              </button>
                              {isOpen ? (
                                <div className="results-narrative-content">
                                  <div className="results-narrative-body">
                                    {section.blocks.map((block, index) => (
                                      <div key={`${section.id}-${index}`} className="results-narrative-copy-block">
                                        {block.title ? <h4>{block.title}</h4> : null}
                                        <p>{block.body}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </section>
                  ) : null}

                  <div className="results-divider" />
                  <div className="results-footer-note">
                    Word Reading Assessment (WRA) is developed by Ventris Learning. Results are intended to support, not replace, professional judgment.
                  </div>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </Container>
  );
}

function buildGroupedCategories(schemas: any[]) {
  for (let i = 0; i < schemas.length; i++) {
    if (schemas[i].name === 'WRA') {
      const groupedCategories: any[] = [];

      for (let j = 0; j < schemas[i].questions?.length; j++) {
        const question = schemas[i].questions[j];
        let categoryIndex = groupedCategories.findIndex((category) => category.name === question.category);

        if (categoryIndex === -1) {
          categoryIndex = groupedCategories.length;
          groupedCategories.push({
            name: question.category,
            subcategories: []
          });
        }

        let subcategoryIndex = groupedCategories[categoryIndex].subcategories.findIndex((subcategory: any) => subcategory.name === question.subcategory);

        if (subcategoryIndex === -1) {
          subcategoryIndex = groupedCategories[categoryIndex].subcategories.length;
          groupedCategories[categoryIndex].subcategories.push({
            name: question.subcategory,
            questions: []
          });
        }

        if (question.discrimination && question.difficulty) {
          groupedCategories[categoryIndex].subcategories[subcategoryIndex].questions.push(question);
        }
      }

      return groupedCategories;
    }
  }

  return [];
}

function resolveNarrativeProfile(test: any, allScoreRows: any[], studentLookup: Map<string, any>) {
  const studentInfo = resolveStudentInfo(test, studentLookup);
  const studentId = studentInfo.id ? String(studentInfo.id) : null;
  const normalizedGrade = normalizeGrade(studentInfo.grade || "");
  const profileBand = narrativeBandFor(normalizedGrade, test.season);
  const screening = resolveTestRiskClassification(test, allScoreRows, studentLookup);

  if (!profileBand || !screening) {
    return {
      showProfileSections: false,
      profile: null
    };
  }

  const cutoffDate = testDateKey(test.completed_timestamp);
  const testRows = allScoreRows
    .filter((row) => {
      const rowStudentId = row.student_id ? String(row.student_id) : null;
      if (!studentId || rowStudentId !== studentId) {
        return false;
      }

      const rowDate = testDateKey(row.completed_timestamp);
      return !!rowDate && rowDate <= cutoffDate;
    })
    .sort((leftRow, rightRow) =>
      new Date(rightRow.completed_timestamp).getTime() - new Date(leftRow.completed_timestamp).getTime()
    );
  const subtestRows = new Map<string, any>();

  testRows.forEach((row) => {
    if (!subtestRows.has(row.subtest) && percentileStatus(row.percentileRank)) {
      subtestRows.set(row.subtest, row);
    }
  });

  const blendStatus = percentileStatus(subtestRows.get("Blending")?.percentileRank);
  const deletionStatus = percentileStatus(subtestRows.get("Deletion")?.percentileRank);
  const decodingStatus = percentileStatus(subtestRows.get("Nonword Reading")?.percentileRank);
  const wordStatus = percentileStatus(subtestRows.get("Word Reading")?.percentileRank);

  let profile: NarrativeProfileRecord | null = null;
  let showProfileSections = false;

  if (profileBand === "KF") {
    if (blendStatus && deletionStatus) {
      const phonStatus = blendStatus === "low" || deletionStatus === "low" ? "low" : "ok";
      profile = WRA_NARRATIVE_PROFILES.KF.find((currentProfile) => currentProfile.screening === screening && currentProfile.phon === phonStatus) || null;
      showProfileSections = !!profile;
    }
  } else if (profileBand === "KW2") {
    const phonCandidates = [blendStatus, deletionStatus].filter(Boolean) as Array<"low" | "ok">;
    if (phonCandidates.length > 0 && decodingStatus && wordStatus) {
      const phonStatus = phonCandidates.includes("low") ? "low" : "ok";
      profile = WRA_NARRATIVE_PROFILES.KW2.find((currentProfile) =>
        currentProfile.screening === screening
        && currentProfile.phon === phonStatus
        && currentProfile.decoding === decodingStatus
        && currentProfile.word === wordStatus
      ) || null;
      showProfileSections = !!profile;
    }
  } else if (profileBand === "G3") {
    if (decodingStatus && wordStatus) {
      profile = WRA_NARRATIVE_PROFILES.G3.find((currentProfile) =>
        currentProfile.screening === screening
        && currentProfile.decoding === decodingStatus
        && currentProfile.word === wordStatus
      ) || null;
      showProfileSections = !!profile;
    }
  }

  return {
    showProfileSections,
    profile
  };
}

function getSchoolYearStart() {
  const today = new Date();
  const schoolYear = today.getMonth() >= SCHOOL_YEAR_START_MONTH ? today.getFullYear() : today.getFullYear() - 1;
  return `${schoolYear}-07-01`;
}

function getTodayDateString() {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const year = today.getFullYear();
  return `${year}-${month}-${day}`;
}

function filterTestsForTimeframe(tests: any[], timeframe: "recent" | "ytd" | "range", startDate?: string, endDate?: string) {
  const sortedTests = tests
    .slice()
    .sort((a, b) => new Date(b.completed_timestamp).getTime() - new Date(a.completed_timestamp).getTime());

  if (timeframe === 'recent') {
    return latestScreeningTestsPerStudent(sortedTests);
  }

  if (timeframe === 'ytd') {
    const schoolYearStart = getSchoolYearStart();
    return sortedTests.filter((test) => testDateKey(test.completed_timestamp) >= schoolYearStart);
  }

  const normalizedStart = startDate || '0000-01-01';
  const normalizedEnd = endDate || '9999-12-31';
  return sortedTests.filter((test) => {
    const key = testDateKey(test.completed_timestamp);
    return key >= normalizedStart && key <= normalizedEnd;
  });
}

function latestTestPerStudent(tests: any[]) {
  const seen = new Set<string>();
  const latest = [];

  for (let i = 0; i < tests.length; i++) {
    const studentId = String(tests[i].student_id);
    if (seen.has(studentId)) {
      continue;
    }

    seen.add(studentId);
    latest.push(tests[i]);
  }

  return latest;
}

function latestScreeningTestsPerStudent(tests: any[]) {
  const latestDateByStudent = new Map<string, string>();

  tests.forEach((test) => {
    const studentId = String(test.student_id);
    const completedDate = testDateKey(test.completed_timestamp);

    if (!latestDateByStudent.has(studentId)) {
      latestDateByStudent.set(studentId, completedDate);
    }
  });

  return tests.filter((test) => {
    const studentId = String(test.student_id);
    return latestDateByStudent.get(studentId) === testDateKey(test.completed_timestamp);
  });
}

function latestTestsPerStudentAndInterval(tests: any[], mode: "month" | "norming") {
  const latest = new Map<string, any>();

  tests
    .slice()
    .sort((a, b) => new Date(b.completed_timestamp).getTime() - new Date(a.completed_timestamp).getTime())
    .forEach((test) => {
      const intervalLabel = aggregateIntervalLabelForTest(test, mode);
      if (!intervalLabel) {
        return;
      }

      const key = `${test.student_id}-${intervalLabel}`;
      if (!latest.has(key)) {
        latest.set(key, test);
      }
    });

  return Array.from(latest.values());
}

function latestScoreRowsForSubtest(rows: any[], subtest: string) {
  const latest = new Map<string, any>();

  rows
    .filter((row) => row.subtest === subtest)
    .filter((row) => isNumericScoreValue(row.percentileRank))
    .sort((a, b) => new Date(b.completed_timestamp).getTime() - new Date(a.completed_timestamp).getTime())
    .forEach((row) => {
      const key = String(row.student_id);
      if (!latest.has(key)) {
        latest.set(key, row);
      }
    });

  return Array.from(latest.values());
}

function latestScoreRowsForSubtestAndInterval(rows: any[], subtest: string, mode: "month" | "norming") {
  const latest = new Map<string, any>();

  rows
    .filter((row) => row.subtest === subtest)
    .filter((row) => isNumericScoreValue(row.scaleScore))
    .sort((a, b) => new Date(b.completed_timestamp).getTime() - new Date(a.completed_timestamp).getTime())
    .forEach((row) => {
      const intervalLabel = aggregateIntervalLabelForRow(row, mode);
      if (!intervalLabel) {
        return;
      }

      const key = `${row.student_id}-${intervalLabel}`;
      if (!latest.has(key)) {
        latest.set(key, row);
      }
    });

  return Array.from(latest.values());
}

function resolveStudentInfo(test: any, studentLookup: Map<string, any>) {
  return studentLookup.get(String(test.student_id)) || {
    id: test.student_id,
    first_name: test.student_first_name,
    last_name: test.student_last_name,
    grade: test.student_grade,
    birthdate: test.student_birthdate
  };
}

function normalizeGrade(grade: string) {
  if (!grade) {
    return grade;
  }
  if (grade === "K" || grade === "1st" || grade === "2nd" || grade === "3rd") {
    return grade;
  }
  const numericMatch = grade.match(/\d+/);
  if (numericMatch) {
    const numericGrade = parseInt(numericMatch[0], 10);
    if (numericGrade <= 0) {
      return "K";
    }
    if (numericGrade === 1) {
      return "1st";
    }
    if (numericGrade === 2) {
      return "2nd";
    }
    if (numericGrade >= 3) {
      return "3rd";
    }
  }
  return grade;
}

function normalizeRiskClassification(classification: any): "High" | "Moderate" | "Low" | null {
  if (!classification) {
    return null;
  }

  const normalized = String(classification).trim().toLowerCase();

  if (normalized === "high" || normalized === "high risk") {
    return "High";
  }

  if (normalized === "moderate" || normalized === "moderate risk") {
    return "Moderate";
  }

  if (normalized === "low" || normalized === "low risk") {
    return "Low";
  }

  return null;
}

function resolveTestRiskClassification(
  test: any,
  allScoreRows: any[],
  studentLookup: Map<string, any>
): "High" | "Moderate" | "Low" | null {
  if (!test) {
    return null;
  }

  const normalizedFinalClassification = String(test.finalClassification || "").trim().toUpperCase();

  if (normalizedFinalClassification === "HIGH RISK") {
    return "High";
  }

  if (normalizedFinalClassification === "MODERATE RISK") {
    return "Moderate";
  }

  if (normalizedFinalClassification === "LOW RISK") {
    return "Low";
  }

  if (normalizedFinalClassification === "N/A") {
    return null;
  }

  const studentInfo = resolveStudentInfo(test, studentLookup);
  const normalizedGrade = normalizeGrade(studentInfo.grade || "");
  const cutpoints = SCREENING_RISK_CUTPOINTS[`${normalizedGrade}|${test.season}`];

  const wordReadingRow = allScoreRows.find((row) =>
    row.testId?.toString() === test.id?.toString()
    && row.subtest === "Word Reading"
    && isNumericScoreValue(row.scaleScore)
  );

  if (cutpoints && wordReadingRow) {
    const scaleScore = Number(wordReadingRow.scaleScore);

    if (scaleScore < cutpoints.highRiskCutPoint) {
      return "High";
    }

    if (scaleScore < cutpoints.moderateRiskCutPoint) {
      return "Moderate";
    }

    return "Low";
  }

  const dysTrue = String(test.dysInterpretation) === "True";
  const pwrrTrue = String(test.pwrrInterpretation) === "True";

  if (dysTrue && pwrrTrue) {
    return "High";
  }

  if (!dysTrue && !pwrrTrue) {
    return "Low";
  }

  return "Moderate";
}

function percentileStatus(value: any): "low" | "ok" | null {
  if (!isNumericScoreValue(value)) {
    return null;
  }

  return Number(value) < 30 ? "low" : "ok";
}

function narrativeBandFor(grade: string, season: string): "KF" | "KW2" | "G3" | null {
  const normalizedGrade = normalizeGrade(grade);

  if (normalizedGrade === "K" && season === "Fall") {
    return "KF";
  }

  if (normalizedGrade === "3rd") {
    return "G3";
  }

  if (normalizedGrade === "K" || normalizedGrade === "1st" || normalizedGrade === "2nd") {
    return "KW2";
  }

  return null;
}

function screeningSubtestsFor(season: string, grade: string) {
  const normalizedGrade = normalizeGrade(grade);
  if (normalizedGrade === "K" && season === "Fall") {
    return ["Deletion", "Blending"];
  }
  if (normalizedGrade === "K" && season === "Winter") {
    return ["Blending", "Word Reading"];
  }
  return ["Word Reading"];
}

function formatDateLabel(cell: any) {
  if (!cell) {
    return "";
  }

  const parsedDate = new Date(cell);
  if (isNaN(parsedDate.getTime())) {
    return cell;
  }

  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  const year = parsedDate.getFullYear();

  return `${month}/${day}/${year}`;
}

function formatMonthLabel(cell: any) {
  if (!cell) {
    return "";
  }

  const parsedDate = new Date(cell);
  if (isNaN(parsedDate.getTime())) {
    return String(cell);
  }

  return parsedDate.toLocaleString('en-US', {
    month: 'short',
    year: 'numeric'
  });
}

function testDateKey(cell: any) {
  if (!cell) {
    return "";
  }

  const parsedDate = new Date(cell);
  if (isNaN(parsedDate.getTime())) {
    return cell;
  }

  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  const year = parsedDate.getFullYear();

  return `${year}-${month}-${day}`;
}

function isNumericScoreValue(value: any) {
  return value !== null && value !== undefined && value !== "" && value !== "N/A" && !isNaN(Number(value));
}

function aggregateIntervalLabelForTest(test: any, mode: "month" | "norming") {
  if (mode === "month") {
    return formatMonthLabel(test.completed_timestamp);
  }

  return test.season || "";
}

function aggregateIntervalLabelForRow(row: any, mode: "month" | "norming") {
  if (mode === "month") {
    return formatMonthLabel(row.completed_timestamp);
  }

  return row.norms || "";
}

function buildAggregateIntervalLabels(tests: any[], mode: "month" | "norming") {
  if (mode === "norming") {
    const seasonOrder = ['Fall', 'Winter', 'Spring'];
    const present = new Set(tests.map((test) => test.season).filter(Boolean));
    return seasonOrder.filter((season) => present.has(season));
  }

  const labelsByKey = new Map<string, string>();

  tests.forEach((test) => {
    const key = testDateKey(test.completed_timestamp).slice(0, 7);
    if (!key || labelsByKey.has(key)) {
      return;
    }

    labelsByKey.set(key, formatMonthLabel(test.completed_timestamp));
  });

  return Array.from(labelsByKey.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, label]) => label);
}

function getFloorCeilingInfo(category: any, answers: any) {
  let consecutiveCorrectCount = 0;
  let consecutiveIncorrectCount = 0;
  const firstEightResults: boolean[] = [];

  for (let j = 0; j < category.subcategories?.length; j++) {
    for (let k = 0; k < category.subcategories[j].questions?.length; k++) {
      const question: any = category.subcategories[j].questions[k];
      if (answers[question.id]) {
        for (let l = 0; l < question.answers.length; l++) {
          if (question.answers[l].id === answers[question.id]) {
            const answer: any = question.answers[l];
            if (firstEightResults.length < 8) {
              firstEightResults.push(!!answer.correct);
            }
            if (answer.correct) {
              if (consecutiveIncorrectCount > 0) {
                consecutiveIncorrectCount = 0;
                consecutiveCorrectCount = 1;
              } else {
                consecutiveCorrectCount += 1;
              }
            } else {
              if (consecutiveCorrectCount > 0) {
                consecutiveCorrectCount = 0;
                consecutiveIncorrectCount = 1;
              } else {
                consecutiveIncorrectCount += 1;
              }
            }
          }
        }
      }
    }
  }

  const firstEightAllCorrect = firstEightResults.length >= 8 && firstEightResults.every((result) => result);
  const firstEightAllIncorrect = firstEightResults.length >= 8 && firstEightResults.every((result) => !result);

  let flag = "";
  if (firstEightAllCorrect || consecutiveCorrectCount >= 8) {
    flag = "Ceiling";
  } else if (firstEightAllIncorrect || consecutiveIncorrectCount >= 8) {
    flag = "Floor";
  }

  return {
    isFloorCeiling: flag !== "",
    flag
  };
}

function calculateScore(test: any, subtest: string, studentInfo: any, maxTheta: number) {
  let scaleScore = 0;
  let standardScore = 0;
  let percentileRank = 0;

  const normalizedGrade = normalizeGrade(studentInfo.grade);
  let gradeCode = "";
  if (normalizedGrade === "K") {
    gradeCode = "KG";
  } else if (normalizedGrade === "1st") {
    gradeCode = "G1";
  } else if (normalizedGrade === "2nd") {
    gradeCode = "G2";
  } else if (normalizedGrade === "3rd") {
    gradeCode = "G3";
  }

  let scoringData: any = [];
  let scoringCode = "";
  if (subtest === "Nonword Reading") {
    scoringData = nre;
    scoringCode = "nre";
  } else if (subtest === "Word Reading") {
    scoringData = wre;
    scoringCode = "wre";
  } else if (subtest === "Blending") {
    scoringData = ble;
    scoringCode = "ble";
  } else if (subtest === "Deletion") {
    scoringData = del;
    scoringCode = "del";
  }

  if (scoringData.length > 0 && gradeCode !== "") {
    let closestIndex = 0;
    let thetaDiff = 9999999;

    for (let j = 0; j < scoringData.length; j++) {
      if (Math.abs(maxTheta - scoringData[j][gradeCode + ' ' + test.season + '_theta_' + scoringCode]) < thetaDiff) {
        thetaDiff = Math.abs(maxTheta - scoringData[j][gradeCode + ' ' + test.season + '_theta_' + scoringCode]);
        closestIndex = j;
      }
    }
    scaleScore = scoringData[closestIndex][gradeCode + ' ' + test.season + '_dss_' + scoringCode];
    standardScore = scoringData[closestIndex][gradeCode + ' ' + test.season + '_ss_' + scoringCode];
    percentileRank = scoringData[closestIndex][gradeCode + ' ' + test.season + '_pr_1_99_' + scoringCode];
  }

  return {
    scaleScore,
    standardScore,
    percentileRank
  };
}

function computeWraResults(tests: any[], categories: any[], studentLookup: Map<string, any>) {
  const allScores: any[] = [];
  const testsFiltered: any[] = [];

  for (let t = 0; t < tests.length; t++) {
    const test = { ...tests[t] };
    const studentInfo = resolveStudentInfo(test, studentLookup);
    const normalizedGrade = normalizeGrade(studentInfo.grade);
    const flaggedSubtests = new Set<string>();
    const answers: any = {};

    for (let i = 0; i < test.answers?.length; i++) {
      answers[test.answers[i].test_schema_question_id] = test.answers[i].test_schema_question_answer_id;
    }

    const subtestThetas: any = {
      nre: 0,
      wre: 0,
      ble: 0,
      del: 0
    };

    for (let i = 0; i < categories.length; i++) {
      const floorCeilingInfo = getFloorCeilingInfo(categories[i], answers);
      if (floorCeilingInfo.isFloorCeiling) {
        flaggedSubtests.add(categories[i].name);
      }

      let thetas: any[] = [];
      let likelihoods: any = {};
      let maxTheta = 0;
      let maxThetaLikelihood = -9999;
      let informationSum = 0;
      let standardError = 0;

      for (let j = 0; j < categories[i].subcategories.length; j++) {
        for (let k = 0; k < categories[i].subcategories[j].questions.length; k++) {
          const question: any = categories[i].subcategories[j].questions[k];
          if (question.id in answers && question.discrimination && question.difficulty) {
            for (let l = 0; l < question.answers.length; l++) {
              if (question.answers[l].id === answers[question.id]) {
                const answer: any = question.answers[l];
                let probabilities: any = {};
                for (let theta = -5; theta <= 5; theta += 0.01) {
                  const probability = 1 / (1 + Math.exp(-1.7 * question.discrimination * (theta - question.difficulty)));
                  probabilities[theta] = answer.correct ? probability : 1 - probability;
                }
                thetas.push(probabilities);
              }
            }
          }
        }
      }

      for (let theta = -5; theta <= 5; theta += 0.01) {
        likelihoods[theta] = 1;
        for (let i = 0; i < thetas.length; i++) {
          likelihoods[theta] *= thetas[i][theta];
        }

        if (likelihoods[theta] > maxThetaLikelihood) {
          maxThetaLikelihood = likelihoods[theta];
          maxTheta = theta;
        }
      }

      const scoreRowIdentity = {
        id: `${test.id}-${categories[i].name}`,
        testId: test.id,
        student_id: test.student_id,
        student_name: `${studentInfo.first_name || test.student_first_name || ''} ${studentInfo.last_name || test.student_last_name || ''}`.trim(),
        student_short_label: `${(studentInfo.first_name || test.student_first_name || '').slice(0, 2)}${(studentInfo.last_name || test.student_last_name || '').slice(0, 1)}`,
        completed_timestamp: test.completed_timestamp,
        completed_date_key: testDateKey(test.completed_timestamp),
        subtest: categories[i].name,
        norms: test.season
      };

      if (maxTheta > -5) {
        if (normalizedGrade === "2nd" && test.season === "Winter" && categories[i].name === "Word Reading") {
          maxTheta = 1.04885752 * maxTheta - 0.3062846;
        } else if (normalizedGrade === "3rd" && test.season === "Spring" && categories[i].name === "Word Reading") {
          maxTheta = 0.97766434 * maxTheta + 0.56662549;
        }

        for (let j = 0; j < categories[i].subcategories.length; j++) {
          for (let k = 0; k < categories[i].subcategories[j].questions.length; k++) {
            const question: any = categories[i].subcategories[j].questions[k];
            if (question.id in answers && question.discrimination && question.difficulty) {
              const probability = 1 / (1 + Math.exp(-1.7 * question.discrimination * (maxTheta - question.difficulty)));
              const information = (Math.pow(1.7 * question.discrimination, 2) * (1 - probability) * probability);
              informationSum += information;
            }
          }
        }

        standardError = informationSum > 0 ? 1 / Math.sqrt(informationSum) : 0;

        const score = calculateScore(test, categories[i].name, studentInfo, maxTheta);
        const scoreConfidenceIntervalLower = calculateScore(test, categories[i].name, studentInfo, maxTheta - 1.96 * standardError);
        const scoreConfidenceIntervalUpper = calculateScore(test, categories[i].name, studentInfo, maxTheta + 1.96 * standardError);

        const scoreRow = {
          ...scoreRowIdentity,
          scaleScore: floorCeilingInfo.isFloorCeiling ? "N/A" : score.scaleScore,
          scaleScoreCI: floorCeilingInfo.isFloorCeiling ? "N/A" : `${scoreConfidenceIntervalLower.scaleScore} - ${scoreConfidenceIntervalUpper.scaleScore}`,
          standardScore: floorCeilingInfo.isFloorCeiling ? "N/A" : score.standardScore,
          standardScoreCI: floorCeilingInfo.isFloorCeiling ? "N/A" : `${scoreConfidenceIntervalLower.standardScore} - ${scoreConfidenceIntervalUpper.standardScore}`,
          percentileRank: floorCeilingInfo.isFloorCeiling ? "N/A" : score.percentileRank,
          percentileRankCI: floorCeilingInfo.isFloorCeiling ? "N/A" : `${scoreConfidenceIntervalLower.percentileRank} - ${scoreConfidenceIntervalUpper.percentileRank}`,
          flag: floorCeilingInfo.isFloorCeiling ? floorCeilingInfo.flag : "",
        };

        allScores.push(scoreRow);

        if (categories[i].name === "Nonword Reading") {
          subtestThetas.nre = maxTheta;
        } else if (categories[i].name === "Word Reading") {
          subtestThetas.wre = maxTheta;
        } else if (categories[i].name === "Blending") {
          subtestThetas.ble = maxTheta;
        } else if (categories[i].name === "Deletion") {
          subtestThetas.del = maxTheta;
        }
      } else if (floorCeilingInfo.isFloorCeiling) {
        allScores.push({
          ...scoreRowIdentity,
          scaleScore: "N/A",
          scaleScoreCI: "N/A",
          standardScore: "N/A",
          standardScoreCI: "N/A",
          percentileRank: "N/A",
          percentileRankCI: "N/A",
          flag: floorCeilingInfo.flag
        });
      }
    }

    test.pwrrFlag = 0;
    test.pwrrCutoff = 0;
    test.dysFlag = 0;
    test.dysCutoff = 0;

    if (normalizedGrade === "K") {
      if (test.season === "Fall") {
        test.pwrrFlag = -1.3749 + -0.828 * subtestThetas.del + -0.5991 * subtestThetas.ble;
        test.pwrrCutoff = -0.478;
        test.dysFlag = -2.9864 + -0.8359 * subtestThetas.del + -0.6229 * subtestThetas.ble;
        test.dysCutoff = -1.65;
      } else if (test.season === "Winter") {
        test.pwrrFlag = -1.5141 + -0.623 * subtestThetas.ble + -1.2998 * subtestThetas.wre;
        test.pwrrCutoff = -0.439739;
        test.dysFlag = -3.0957 + -0.4999 * subtestThetas.ble + -1.1774 * subtestThetas.wre;
        test.dysCutoff = -1.600208;
      } else if (test.season === "Spring") {
        test.pwrrFlag = -2.242 + -2.2315 * subtestThetas.wre;
        test.pwrrCutoff = -0.4790195;
        test.dysFlag = -4.4823 + -2.3147 * subtestThetas.wre;
        test.dysCutoff = -1.7153412;
      }
    } else if (normalizedGrade === "1st") {
      if (test.season === "Fall") {
        test.pwrrFlag = -1.7147 + -3.1426 * subtestThetas.wre;
        test.pwrrCutoff = -0.6280326;
        test.dysFlag = -4.3025 + -3.6139 * subtestThetas.wre;
        test.dysCutoff = -1.5756004;
      } else if (test.season === "Winter") {
        test.pwrrFlag = 0.07712 + -4.07885 * subtestThetas.wre;
        test.pwrrCutoff = -0.5154523;
        test.dysFlag = -2.4722 + -3.5014 * subtestThetas.wre;
        test.dysCutoff = -1.9645652;
      } else if (test.season === "Spring") {
        test.pwrrFlag = 0.7128 + -4.8007 * subtestThetas.wre;
        test.pwrrCutoff = -0.4955694;
        test.dysFlag = -2.347 + -2.8805 * subtestThetas.wre;
        test.dysCutoff = -1.9294146;
      }
    } else if (normalizedGrade === "2nd") {
      if (test.season === "Fall") {
        test.pwrrFlag = 1.252 + -2.6942 * subtestThetas.wre;
        test.pwrrCutoff = -0.4287528;
        test.dysFlag = -0.66558 + -3.1316 * subtestThetas.wre;
        test.dysCutoff = -1.7863947;
      } else if (test.season === "Winter") {
        test.pwrrFlag = 2.0116 + -3.9969 * subtestThetas.wre;
        test.pwrrCutoff = -0.4559537;
        test.dysFlag = -0.2782 + -4.2165 * subtestThetas.wre;
        test.dysCutoff = -1.48187645;
      } else if (test.season === "Spring") {
        test.pwrrFlag = 3.3815 + -4.6224 * subtestThetas.wre;
        test.pwrrCutoff = -0.111554;
        test.dysFlag = 0.5267 + -3.9798 * subtestThetas.wre;
        test.dysCutoff = -1.6117772;
      }
    } else if (normalizedGrade === "3rd") {
      if (test.season === "Fall") {
        test.pwrrFlag = 2.4348 + -2.8995 * subtestThetas.wre;
        test.pwrrCutoff = -0.2824386;
        test.dysFlag = 0.7151 + -3.0383 * subtestThetas.wre;
        test.dysCutoff = -1.7789737;
      } else if (test.season === "Winter") {
        test.pwrrFlag = 4.468 + -3.3563 * subtestThetas.wre;
        test.pwrrCutoff = -0.2210173;
        test.dysFlag = 2.5387 + -3.1855 * subtestThetas.wre;
        test.dysCutoff = -1.5379891;
      } else if (test.season === "Spring") {
        test.pwrrFlag = 9.2099 + -5.0149 * subtestThetas.wre;
        test.pwrrCutoff = 0.008115222;
        test.dysFlag = 5.7115 + -4.4963 * subtestThetas.wre;
        test.dysCutoff = -1.61687139;
      }
    }

    test.dysInterpretation = (test.dysFlag > test.dysCutoff) ? "True" : "False";
    test.pwrrInterpretation = (test.pwrrFlag > test.pwrrCutoff) ? "True" : "False";

    const isKindergartenFallWinterEdgeCase =
      normalizedGrade === "K"
      && (test.season === "Fall" || test.season === "Winter")
      && test.dysInterpretation === "True"
      && test.pwrrInterpretation === "False";

    if (test.dysInterpretation === "True" && test.pwrrInterpretation === "True") {
      test.finalClassification = "High Risk";
    } else if (isKindergartenFallWinterEdgeCase) {
      test.finalClassification = "High Risk";
    } else if (test.dysInterpretation === "False" && test.pwrrInterpretation === "False") {
      test.finalClassification = "Low Risk";
    } else {
      test.finalClassification = "Moderate Risk";
    }

    const screeningSubtests = screeningSubtestsFor(test.season, studentInfo.grade);
    const hasScreeningFloorCeiling = screeningSubtests.some((subtest) => flaggedSubtests.has(subtest));
    if (hasScreeningFloorCeiling) {
      test.finalClassification = "N/A";
    }

    testsFiltered.push(test);
  }

  return {
    tests: testsFiltered,
    allScoreRows: allScores
  };
}

export default ClinicianResults;
